import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AdminSidebar } from '../components/AdminSidebar';
import { CsvImportModal } from '../components/CsvImportModal';
import { EmailTemplateModal } from '../components/EmailTemplateModal';
import { EmailConfigModal } from '../components/EmailConfigModal';
import { EmailTemplate, SenderAccount, EmailSettings } from '../types';
import { renderEmailTemplate, getSampleVoter } from '../utils/emailTemplateEngine';
import { generateVoterPassword } from '../utils/voterAuth';
import { DemographicResultsSection } from '../components/DemographicResultsSection';
import { OfficialReportPdfModal } from '../components/OfficialReportPdfModal';
import { api } from '../services/api';
import {
  ElectionSummary,
  ElectionData,
  Position,
  Candidate,
  VoterProfile,
  DemographicTurnout,
  LiveResultsResponse,
} from '../types';
import {
  Users,
  Award,
  Mail,
  Clock,
  Calendar,
  BarChart3,
  Settings,
  LayoutDashboard,
  Play,
  Pause,
  StopCircle,
  Download,
  Search,
  PlusCircle,
  Trash2,
  Copy,
  Edit,
  Eye,
  ExternalLink,
  Lock,
  Unlock,
  Key,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  UploadCloud,
  X,
  Send,
  RotateCcw,
  Filter,
  ArrowUpDown,
  GripVertical,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Printer,
  ShieldCheck,
  Globe,
  Share2,
  Ban,
  ShieldAlert,
} from 'lucide-react';
import { setSiteMeta } from '../utils/meta';

export const AdminDashboardPage: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Navigation tab from URL query params (default to overview)
  const currentTab = searchParams.get('tab') || 'overview';
  const setTab = (tab: string) => {
    setSearchParams({ tab });
  };

  // ---------------------------------------------------------------------------
  // GLOBAL STATE
  // ---------------------------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [election, setElection] = useState<ElectionSummary>(api.getElectionSettingsSync());
  const [voters, setVoters] = useState<VoterProfile[]>([]);
  const [rolesData, setRolesData] = useState<ElectionData>({});
  const [analytics, setAnalytics] = useState<{
    byLevel: DemographicTurnout[];
    byGender: DemographicTurnout[];
    byHall: DemographicTurnout[];
  } | null>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);

  // Feedback notifications
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // ---------------------------------------------------------------------------
  // LOAD CORE DATA
  // ---------------------------------------------------------------------------
  const refreshAllData = async () => {
    setLoading(true);
    const electionsList = await api.getAdminElections();
    let currentElection = election;
    if (electionsList.length > 0) {
      setElection(electionsList[0]);
      currentElection = electionsList[0];
    }
    const voterList = await api.getVoters(currentElection.id);
    setVoters(voterList);

    const roles = await api.getElectionData(undefined, currentElection.id);
    setRolesData(roles);

    const stats = await api.getDemographicAnalytics(currentElection.id);
    setAnalytics(stats);

    setAuditLogs(api.getAuditLogs());
    setLoading(false);
  };

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    refreshAllData();
  }, [admin, navigate]);

  useEffect(() => {
    if (election && election.title) {
      document.title = election.title + ' - Electpoll Admin';
    }
  }, [election?.title]);

  // Calculated metrics
  const totalAccredited = voters.length;
  const totalVoted = voters.filter((v) => v.has_voted).length;
  const turnoutPercent = totalAccredited > 0 ? parseFloat(((totalVoted / totalAccredited) * 100).toFixed(1)) : 0;
  const totalEmailsSent = voters.reduce((acc, v) => acc + (v.email_sent_count || 0), 0);

  // Status handler
  const handleStatusChange = async (newStatus: 'active' | 'paused' | 'ended' | 'draft') => {
    await api.updateElectionStatus(election.id, newStatus);
    setElection((prev) => ({ ...prev, status: newStatus }));
    showToast(`Election status switched to ${newStatus.toUpperCase()}`);
    setAuditLogs(api.getAuditLogs());
  };

  // ---------------------------------------------------------------------------
  // TAB 1: OVERVIEW HELPERS
  // ---------------------------------------------------------------------------
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [resultsPasswordInput, setResultsPasswordInput] = useState('');

  const handleSaveResultsProtection = async (enable: boolean) => {
    await api.updateResultsProtection(election.id, enable, resultsPasswordInput);
    setElection((prev) => ({
      ...prev,
      isResultsProtected: enable,
      resultsPassword: enable ? resultsPasswordInput : '',
    }));
    setPasswordModalOpen(false);
    showToast(enable ? 'Results password protection enabled' : 'Results made public');
  };

  // ---------------------------------------------------------------------------
  // TAB 2: ACCREDITED VOTERS STATE & HANDLERS
  // ---------------------------------------------------------------------------
  const [voterSearch, setVoterSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [genderFilter, setGenderFilter] = useState('ALL');

  // CSV Import Modal
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  // Recent batch tracking for granular export
  const [recentBatchVoters, setRecentBatchVoters] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('electpoll_recent_batch_voters');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportCustomCount, setExportCustomCount] = useState<number>(15);

  // Add Single Voter Modal
  const [addVoterModalOpen, setAddVoterModalOpen] = useState(false);
  const [newVoterMatric, setNewVoterMatric] = useState('');
  const [newVoterName, setNewVoterName] = useState('');
  const [newVoterEmail, setNewVoterEmail] = useState('');
  const [newVoterPassword, setNewVoterPassword] = useState('');
  const [newVoterLevel, setNewVoterLevel] = useState('100');
  const [newVoterGender, setNewVoterGender] = useState('Male');
  const [newVoterHall, setNewVoterHall] = useState('Independence Hall');

  // Edit Single Voter Modal
  const [editVoterModalOpen, setEditVoterModalOpen] = useState(false);
  const [editingVoter, setEditingVoter] = useState<VoterProfile | null>(null);
  const [editVoterMatric, setEditVoterMatric] = useState('');
  const [editVoterName, setEditVoterName] = useState('');
  const [editVoterEmail, setEditVoterEmail] = useState('');
  const [editVoterPassword, setEditVoterPassword] = useState('');
  const [editVoterLevel, setEditVoterLevel] = useState('100');
  const [editVoterGender, setEditVoterGender] = useState('Male');
  const [editVoterHall, setEditVoterHall] = useState('Independence Hall');
  const [editVoterDepartment, setEditVoterDepartment] = useState('Faculty of Law');
  const [editVoterHasVoted, setEditVoterHasVoted] = useState(false);
  const [editVoterLoading, setEditVoterLoading] = useState(false);

  const filteredVoters = useMemo(() => {
    return voters.filter((v) => {
      const q = voterSearch.toLowerCase();
      const matchesSearch =
        v.matric.toLowerCase().includes(q) ||
        v.name.toLowerCase().includes(q) ||
        v.email.toLowerCase().includes(q);

      const matchesLevel = levelFilter === 'ALL' || v.level === levelFilter;
      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'VOTED' && v.has_voted) ||
        (statusFilter === 'NOT_VOTED' && !v.has_voted);
      const matchesGender =
        genderFilter === 'ALL' ||
        (v.gender || '').toLowerCase() === genderFilter.toLowerCase();

      return matchesSearch && matchesLevel && matchesStatus && matchesGender;
    });
  }, [voters, voterSearch, levelFilter, statusFilter, genderFilter]);



  const handleAddSingleVoter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVoterMatric || !newVoterName) return;

    const res = await api.createVoter(election.id, {
      matric: newVoterMatric.trim(),
      name: newVoterName.trim(),
      email: newVoterEmail.trim() || `${newVoterMatric.trim()}@student.ui.edu.ng`,
      password: newVoterPassword.trim() || undefined,
      level: newVoterLevel,
      gender: newVoterGender,
      hall: newVoterHall,
    });

    if (res.success) {
      showToast(`Voter ${newVoterMatric} enrolled successfully.`);
      setAddVoterModalOpen(false);
      setNewVoterMatric('');
      setNewVoterName('');
      setNewVoterEmail('');
      setNewVoterPassword('');
      const updated = await api.getVoters(election.id);
      setVoters(updated);
      const electionsList = await api.getAdminElections();
      if (electionsList.length > 0) {
        setElection(electionsList[0]);
      }
    } else {
      alert(res.message || 'Error adding voter');
    }
  };

  const handleDeleteVoter = async (matric: string) => {
    if (window.confirm(`Are you sure you want to remove voter ${matric} from accredited roster?`)) {
      await api.deleteVoter(matric, election.id);
      setVoters((prev) => prev.filter((v) => v.matric !== matric));
      showToast(`Voter ${matric} removed.`);
      const updated = await api.getVoters(election.id);
      setVoters(updated);
    }
  };

  const handleOpenEditVoter = (voter: VoterProfile) => {
    setEditingVoter(voter);
    setEditVoterMatric(voter.matric);
    setEditVoterName(voter.name);
    setEditVoterEmail(voter.email || '');
    setEditVoterPassword(voter.password || '');
    setEditVoterLevel(voter.level?.replace('L', '') || '100');
    setEditVoterGender(voter.gender || 'Male');
    setEditVoterHall(voter.hall || 'Independence Hall');
    setEditVoterDepartment(voter.department || 'Faculty of Law');
    setEditVoterHasVoted(Boolean(voter.has_voted));
    setEditVoterModalOpen(true);
  };

  const handleSaveEditVoter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVoter) return;
    if (!editVoterMatric.trim() || !editVoterName.trim()) {
      alert('Matric Number and Student Name are required.');
      return;
    }

    setEditVoterLoading(true);
    const res = await api.updateVoter(election.id, editingVoter.matric, {
      id: editingVoter.id,
      matric: editVoterMatric.trim(),
      name: editVoterName.trim(),
      email: editVoterEmail.trim(),
      password: editVoterPassword.trim(),
      level: editVoterLevel,
      gender: editVoterGender,
      hall: editVoterHall.trim(),
      department: editVoterDepartment.trim(),
      has_voted: editVoterHasVoted,
    });
    setEditVoterLoading(false);

    if (res.success) {
      showToast(`Voter ${editVoterMatric.trim()} updated successfully!`);
      setEditVoterModalOpen(false);
      setEditingVoter(null);
      const updated = await api.getVoters(election.id);
      setVoters(updated);
    } else {
      alert(res.message || 'Error updating voter details');
    }
  };

  const handleDeleteAllVoters = async () => {
    if (voters.length === 0) return;
    if (
      window.confirm(
        `Are you sure you want to delete ALL ${voters.length} accredited voters? This will permanently wipe the voter roster for this election.`
      )
    ) {
      setLoading(true);
      await api.deleteAllVoters(election.id);
      setVoters([]);
      showToast(`All accredited voters successfully removed.`);
      const updated = await api.getVoters(election.id);
      setVoters(updated);
      setLoading(false);
    }
  };

  const downloadCsvFile = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportFullRosterCsv = () => {
    let csv = 'Matric No,Student Name,Email,Password,Level,Gender,Hall,Vote Status,Email Sent Count\n';
    filteredVoters.forEach((v) => {
      csv += `"${v.matric}","${v.name}","${v.email}","${v.password || 'pass123'}","${v.level}L","${v.gender || 'Unspecified'}","${v.hall || 'Not Specified'}","${v.has_voted ? 'VOTED' : 'NOT VOTED'}",${v.email_sent_count || 0}\n`;
    });
    downloadCsvFile(`accredited-voters-${election.slug || 'election'}.csv`, csv);
    showToast(`Exported full roster of ${filteredVoters.length} voters.`);
  };

  const handleExportRecentBatchCsv = (batchList?: any[]) => {
    const list = batchList || recentBatchVoters;
    if (!list || list.length === 0) {
      alert("No recent batch records found. You can choose 'Export Latest Enrolled Students' instead.");
      return;
    }
    let csv = 'Matric No,Student Name,Email,Voting Password,Academic Level,Gender,Hall of Residence,Department\n';
    list.forEach((v: any) => {
      csv += `"${v.matric}","${v.name}","${v.email}","${v.password || 'pass123'}","${v.level || '100'}L","${v.gender || 'Unspecified'}","${v.hall || 'Not Specified'}","${v.department || 'Not Specified'}"\n`;
    });
    downloadCsvFile(`recent-batch-credentials-${list.length}-${election.slug || 'election'}.csv`, csv);
    showToast(`Exported ${list.length} credentials from recent batch.`);
  };

  const handleExportLatestAddedCsv = (count: number) => {
    const safeCount = Math.max(1, Math.min(count, voters.length));
    const slice = voters.slice(0, safeCount);
    let csv = 'Matric No,Student Name,Email,Voting Password,Academic Level,Gender,Hall of Residence,Department,Vote Status\n';
    slice.forEach((v) => {
      csv += `"${v.matric}","${v.name}","${v.email}","${v.password || 'pass123'}","${v.level}L","${v.gender || 'Unspecified'}","${v.hall || 'Not Specified'}","${v.department || 'Not Specified'}","${v.has_voted ? 'VOTED' : 'NOT VOTED'}"\n`;
    });
    downloadCsvFile(`latest-${safeCount}-enrolled-voters-${election.slug || 'election'}.csv`, csv);
    showToast(`Exported latest ${safeCount} enrolled voter records.`);
  };

  // ---------------------------------------------------------------------------
  // TAB 3: CANDIDATE DETAILS STATE & HANDLERS
  // ---------------------------------------------------------------------------
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRoleKey, setEditingRoleKey] = useState<string | null>(null);
  const [roleNameInput, setRoleNameInput] = useState('');
  const [roleKeyInput, setRoleKeyInput] = useState('');
  const [roleMaxInput, setRoleMaxInput] = useState(1);
  const [roleLevelsInput, setRoleLevelsInput] = useState<string[]>(['100', '200', '300', '400', '500']);

  // Candidate Modal
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);
  const [targetRoleKey, setTargetRoleKey] = useState<string>('');
  const [editingCandidateId, setEditingCandidateId] = useState<string | null>(null);
  const [candidateNameInput, setCandidateNameInput] = useState('');
  const [candidateNicknameInput, setCandidateNicknameInput] = useState('');
  const [candidateImageInput, setCandidateImageInput] = useState('');
  const [candidateBioInput, setCandidateBioInput] = useState('');
  const [candidateAbstainInput, setCandidateAbstainInput] = useState(false);
  const [candidateDisqualifiedInput, setCandidateDisqualifiedInput] = useState(false);
  const [candidateDisqualifyReasonInput, setCandidateDisqualifyReasonInput] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Quick Disqualify Dialog Modal state
  const [quickDisqualifyModalOpen, setQuickDisqualifyModalOpen] = useState(false);
  const [quickDisqualifyCandidate, setQuickDisqualifyCandidate] = useState<{ roleKey: string; cand: Candidate } | null>(null);
  const [quickDisqualifyReason, setQuickDisqualifyReason] = useState('');
  const [savingDisqualification, setSavingDisqualification] = useState(false);

  // Open Role Modal (Add or Edit)
  const openRoleModal = (roleKey?: string) => {
    if (roleKey && rolesData[roleKey]) {
      setEditingRoleKey(roleKey);
      setRoleNameInput(rolesData[roleKey].name);
      setRoleKeyInput(roleKey);
      setRoleMaxInput(rolesData[roleKey].max || 1);
      setRoleLevelsInput(rolesData[roleKey].levels || ['100', '200', '300', '400', '500']);
    } else {
      setEditingRoleKey(null);
      setRoleNameInput('');
      setRoleKeyInput('');
      setRoleMaxInput(1);
      setRoleLevelsInput(['100', '200', '300', '400', '500']);
    }
    setRoleModalOpen(true);
  };

  // Save Role
  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleNameInput.trim()) return;

    const key = (editingRoleKey || roleKeyInput.trim() || roleNameInput.replace(/[^a-zA-Z0-9]/g, '')).trim();
    const updatedRoles = { ...rolesData };

    if (editingRoleKey && editingRoleKey !== key) {
      delete updatedRoles[editingRoleKey];
    }

    updatedRoles[key] = {
      name: roleNameInput.trim(),
      slug: key,
      max: Number(roleMaxInput) || 1,
      levels: roleLevelsInput,
      candidates: rolesData[editingRoleKey || key]?.candidates || [
        { id: `reject-${key}`, name: 'No', image: '/assets/images/indecisive.png', isAbstain: true },
        { id: `indecisive-${key}`, name: 'Indecisive', image: '/assets/images/indecisive.png', isAbstain: true },
      ],
    };

    setRolesData(updatedRoles);
    const res = await api.saveElectionRoles(updatedRoles, election.id);
    if (res?.data) {
      setRolesData(res.data);
    }
    setRoleModalOpen(false);
    showToast(`Role "${roleNameInput}" saved with eligibility rules.`);
  };

  // Delete Role
  const handleDeleteRole = async (roleKey: string) => {
    if (window.confirm(`Are you sure you want to delete role "${rolesData[roleKey]?.name}" and all its candidates?`)) {
      const posToDelete = rolesData[roleKey];
      // 1. Permanently delete from Neon PostgreSQL
      await api.deleteRole(roleKey, posToDelete?.id, election.id);

      // 2. Update local state
      const updated = { ...rolesData };
      delete updated[roleKey];
      setRolesData(updated);
      localStorage.setItem('electpoll_positions_data', JSON.stringify(updated));

      // 3. Background sync to guarantee audit log & consistency
      api.saveElectionRoles(updated, election.id).catch(() => {});
      showToast('Role removed.');
    }
  };

  // Open Candidate Modal
  const openCandidateModal = (roleKey: string, cand?: Candidate) => {
    setTargetRoleKey(roleKey);
    if (cand) {
      setEditingCandidateId(cand.id);
      setCandidateNameInput(cand.name);
      setCandidateNicknameInput(cand.nickname || '');
      setCandidateImageInput(cand.image);
      setCandidateBioInput(cand.bio || '');
      setCandidateAbstainInput(!!cand.isAbstain);
      setCandidateDisqualifiedInput(Boolean(cand.isDisqualified));
      setCandidateDisqualifyReasonInput(cand.disqualificationReason || '');
    } else {
      setEditingCandidateId(null);
      setCandidateNameInput('');
      setCandidateNicknameInput('');
      setCandidateImageInput('/assets/images/userdefault.png');
      setCandidateBioInput('');
      setCandidateAbstainInput(false);
      setCandidateDisqualifiedInput(false);
      setCandidateDisqualifyReasonInput('');
    }
    setCandidateModalOpen(true);
  };

  // Quick Disqualify / Reinstate action
  const handleQuickToggleDisqualify = (roleKey: string, cand: Candidate) => {
    if (cand.isDisqualified) {
      if (window.confirm(`Reinstate "${cand.name}" to active ballot status? Voters will be able to cast votes for this candidate.`)) {
        executeDisqualifyUpdate(roleKey, cand.id, false, '');
      }
    } else {
      setQuickDisqualifyCandidate({ roleKey, cand });
      setQuickDisqualifyReason('');
      setQuickDisqualifyModalOpen(true);
    }
  };

  const executeDisqualifyUpdate = async (roleKey: string, candidateId: string, isDisqualified: boolean, reason: string) => {
    try {
      setSavingDisqualification(true);
      // 1. Optimistically update local rolesData
      const updatedRoles = { ...rolesData };
      if (updatedRoles[roleKey]?.candidates) {
        updatedRoles[roleKey].candidates = updatedRoles[roleKey].candidates.map((c) => {
          if (c.id === candidateId) {
            return {
              ...c,
              isDisqualified,
              disqualificationReason: isDisqualified ? reason : undefined,
            };
          }
          return c;
        });
        setRolesData(updatedRoles);
      }

      // 2. Direct atomic PATCH to Neon backend
      const res = await api.updateCandidateDisqualification(candidateId, isDisqualified, reason);
      if (res.success) {
        showToast(isDisqualified ? 'Candidate disqualified mid-election.' : 'Candidate reinstated successfully.');
      } else {
        // Fallback sync full election roles
        await api.saveElectionRoles(updatedRoles, election.id);
        showToast(isDisqualified ? 'Candidate marked disqualified.' : 'Candidate reinstated.');
      }
    } catch (err: any) {
      showToast(`Error updating status: ${err.message}`);
    } finally {
      setSavingDisqualification(false);
      setQuickDisqualifyModalOpen(false);
      setQuickDisqualifyCandidate(null);
    }
  };

  // Upload image to Cloudinary
  const handleImageFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    const res = await api.uploadImageToCloudinary(file);
    if (res.success && res.url) {
      setCandidateImageInput(res.url);
      showToast('Image uploaded successfully to Cloudinary');
    } else {
      // Fallback: create object URL for instantaneous preview
      const localUrl = URL.createObjectURL(file);
      setCandidateImageInput(localUrl);
      showToast('Using local image preview');
    }
    setUploadingImage(false);
  };

  // Save Candidate
  const handleSaveCandidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidateNameInput.trim() || !targetRoleKey) return;

    const updatedRoles = { ...rolesData };
    const currentCandidates = [...(updatedRoles[targetRoleKey]?.candidates || [])];

    if (editingCandidateId) {
      const idx = currentCandidates.findIndex((c) => c.id === editingCandidateId);
      if (idx !== -1) {
        currentCandidates[idx] = {
          ...currentCandidates[idx],
          name: candidateNameInput.trim(),
          nickname: candidateNicknameInput.trim() || undefined,
          image: candidateImageInput || '/assets/images/userdefault.png',
          bio: candidateBioInput.trim() || undefined,
          isAbstain: candidateAbstainInput,
          isDisqualified: candidateDisqualifiedInput,
          disqualificationReason: candidateDisqualifiedInput ? (candidateDisqualifyReasonInput.trim() || undefined) : undefined,
        };
      }
    } else {
      const newCand: Candidate = {
        id: candidateNameInput.replace(/[^a-zA-Z0-9]/g, '') + Date.now().toString().slice(-4),
        name: candidateNameInput.trim(),
        nickname: candidateNicknameInput.trim() || undefined,
        image: candidateImageInput || '/assets/images/userdefault.png',
        bio: candidateBioInput.trim() || undefined,
        isAbstain: candidateAbstainInput,
        isDisqualified: candidateDisqualifiedInput,
        disqualificationReason: candidateDisqualifiedInput ? (candidateDisqualifyReasonInput.trim() || undefined) : undefined,
      };
      // Insert before abstain options
      const nonAbstains = currentCandidates.filter((c) => !c.isAbstain);
      const abstains = currentCandidates.filter((c) => c.isAbstain);
      currentCandidates.splice(0, currentCandidates.length, ...nonAbstains, newCand, ...abstains);
    }

    updatedRoles[targetRoleKey] = {
      ...updatedRoles[targetRoleKey],
      candidates: currentCandidates,
    };

    setRolesData(updatedRoles);
    const res = await api.saveElectionRoles(updatedRoles, election.id);
    if (res?.data) {
      setRolesData(res.data);
    }
    setCandidateModalOpen(false);
    showToast(`Candidate "${candidateNameInput}" saved.`);
  };

  // Delete Candidate
  const handleDeleteCandidate = async (roleKey: string, candId: string) => {
    if (window.confirm('Delete this candidate option?')) {
      // 1. Permanently delete from Neon PostgreSQL
      await api.deleteCandidate(candId);

      // 2. Update local state
      const updatedRoles = { ...rolesData };
      if (updatedRoles[roleKey]) {
        updatedRoles[roleKey] = {
          ...updatedRoles[roleKey],
          candidates: updatedRoles[roleKey].candidates.filter((c) => c.id !== candId),
        };
      }
      setRolesData(updatedRoles);
      localStorage.setItem('electpoll_positions_data', JSON.stringify(updatedRoles));

      // 3. Background sync to guarantee audit log & consistency
      api.saveElectionRoles(updatedRoles, election.id).catch(() => {});
      showToast('Candidate removed.');
    }
  };

  // ---------------------------------------------------------------------------
  // CANDIDATE & ROLE DRAG & DROP AND ORDERING HANDLERS
  // ---------------------------------------------------------------------------
  const [draggingRoleIndex, setDraggingRoleIndex] = useState<number | null>(null);
  const [dragOverRoleIndex, setDragOverRoleIndex] = useState<number | null>(null);
  const [draggingCand, setDraggingCand] = useState<{ roleKey: string; index: number } | null>(null);
  const [dragOverCand, setDragOverCand] = useState<{ roleKey: string; index: number } | null>(null);

  // --- ROLE DRAG HANDLERS ---
  const handleRoleDragStart = (e: React.DragEvent, index: number) => {
    // If a candidate drag is underway, do not drag role container
    if (draggingCand) return;
    setDraggingRoleIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'ROLE', index }));
    } catch {}
  };

  const handleRoleDragOver = (e: React.DragEvent, index: number) => {
    if (draggingRoleIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverRoleIndex !== index) {
      setDragOverRoleIndex(index);
    }
  };

  const handleRoleDrop = async (e: React.DragEvent, dropIndex: number) => {
    if (draggingRoleIndex === null) return;
    e.preventDefault();
    setDragOverRoleIndex(null);
    if (draggingRoleIndex === dropIndex) {
      setDraggingRoleIndex(null);
      return;
    }

    const fromIndex = draggingRoleIndex;
    setDraggingRoleIndex(null);

    const entries = Object.entries(rolesData);
    const [moved] = entries.splice(fromIndex, 1);
    entries.splice(dropIndex, 0, moved);
    const updatedRoles = Object.fromEntries(entries);

    setRolesData(updatedRoles);
    localStorage.setItem('electpoll_positions_data', JSON.stringify(updatedRoles));
    showToast(`Updated role order: "${moved[1]?.name || moved[0]}" is now ballot position #${dropIndex + 1}.`);

    try {
      const res = await api.saveElectionRoles(updatedRoles, election.id);
      if (res?.data) {
        setRolesData(res.data);
      }
    } catch (err) {
      console.error('Failed to save role reordering:', err);
    }
  };

  const handleRoleDragEnd = () => {
    setDraggingRoleIndex(null);
    setDragOverRoleIndex(null);
  };

  const handleMoveRole = async (index: number, direction: 'up' | 'down') => {
    const entries = Object.entries(rolesData);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= entries.length) return;

    const [moved] = entries.splice(index, 1);
    entries.splice(targetIndex, 0, moved);
    const updatedRoles = Object.fromEntries(entries);

    setRolesData(updatedRoles);
    localStorage.setItem('electpoll_positions_data', JSON.stringify(updatedRoles));
    showToast(`Moved role "${moved[1]?.name || moved[0]}" ${direction === 'up' ? 'higher (# ' + (targetIndex + 1) + ')' : 'lower (# ' + (targetIndex + 1) + ')'}.`);

    try {
      const res = await api.saveElectionRoles(updatedRoles, election.id);
      if (res?.data) {
        setRolesData(res.data);
      }
    } catch (err) {
      console.error('Failed to save role reordering:', err);
    }
  };

  // --- CANDIDATE DRAG HANDLERS ---
  const handleCandidateDragStart = (e: React.DragEvent, roleKey: string, index: number) => {
    e.stopPropagation();
    setDraggingCand({ roleKey, index });
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', JSON.stringify({ roleKey, index }));
    } catch {}
  };

  const handleCandidateDragOver = (e: React.DragEvent, roleKey: string, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggingCand || draggingCand.roleKey !== roleKey) return;
    e.dataTransfer.dropEffect = 'move';
    if (dragOverCand?.index !== index || dragOverCand?.roleKey !== roleKey) {
      setDragOverCand({ roleKey, index });
    }
  };

  const handleCandidateDrop = async (e: React.DragEvent, roleKey: string, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCand(null);
    if (!draggingCand || draggingCand.roleKey !== roleKey || draggingCand.index === dropIndex) {
      setDraggingCand(null);
      return;
    }

    const fromIndex = draggingCand.index;
    setDraggingCand(null);

    const updatedCandidates = [...(rolesData[roleKey]?.candidates || [])];
    const [moved] = updatedCandidates.splice(fromIndex, 1);
    updatedCandidates.splice(dropIndex, 0, moved);

    const updatedRoles = {
      ...rolesData,
      [roleKey]: {
        ...rolesData[roleKey],
        candidates: updatedCandidates,
      },
    };

    setRolesData(updatedRoles);
    localStorage.setItem('electpoll_positions_data', JSON.stringify(updatedRoles));
    showToast(`Updated candidate order in ${rolesData[roleKey]?.name || roleKey}.`);

    try {
      await api.saveElectionRoles(updatedRoles, election.id);
    } catch (err) {
      console.error('Failed to save candidate reordering:', err);
    }
  };

  const handleCandidateDragEnd = (e?: React.DragEvent) => {
    if (e) e.stopPropagation();
    setDraggingCand(null);
    setDragOverCand(null);
  };

  const handleMoveCandidate = async (roleKey: string, index: number, direction: 'prev' | 'next') => {
    const candidates = [...(rolesData[roleKey]?.candidates || [])];
    const targetIndex = direction === 'prev' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= candidates.length) return;

    const [moved] = candidates.splice(index, 1);
    candidates.splice(targetIndex, 0, moved);

    const updatedRoles = {
      ...rolesData,
      [roleKey]: {
        ...rolesData[roleKey],
        candidates,
      },
    };

    setRolesData(updatedRoles);
    localStorage.setItem('electpoll_positions_data', JSON.stringify(updatedRoles));
    showToast(`Moved ${moved.name} ${direction === 'prev' ? 'up/left' : 'down/right'}.`);

    try {
      await api.saveElectionRoles(updatedRoles, election.id);
    } catch (err) {
      console.error('Failed to save candidate reordering:', err);
    }
  };

  // ---------------------------------------------------------------------------
  // TAB 4: EMAILS BROADCAST STATE & HANDLERS
  // ---------------------------------------------------------------------------
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [emailSettings, setEmailSettings] = useState<EmailSettings>(() => api.getEmailSettings(election.title));
  const [emailTemplates, setEmailTemplates] = useState<Record<string, EmailTemplate>>(() => api.getEmailTemplates(election.title));
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [lastBroadcastSummary, setLastBroadcastSummary] = useState<string | null>(null);

  const handleSaveEmailSettings = async (updated: EmailSettings) => {
    await api.saveEmailSettings(updated);
    setEmailSettings(updated);
    showToast('Email delivery and Gmail rotation settings updated!');
  };

  const handleSaveEmailTemplates = async (updated: Record<string, EmailTemplate>) => {
    await api.saveEmailTemplates(updated);
    setEmailTemplates(updated);
    showToast('Email templates saved successfully!');
  };

  const handleTestSenderAccount = async (account: SenderAccount) => {
    return await api.testSenderAccount(account, election.supportEmail || 'admin@electpoll.com');
  };
  const [broadcastTemplate, setBroadcastTemplate] = useState('credentials');
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [broadcastSearch, setBroadcastSearch] = useState('');
  const [broadcastFilter, setBroadcastFilter] = useState<'ALL' | 'UNSENT' | 'SENT' | 'UNVOTED' | 'VOTED'>('ALL');
  const [broadcastSort, setBroadcastSort] = useState<'SENT_ASC' | 'SENT_DESC' | 'NAME' | 'MATRIC'>('SENT_ASC');
  const [selectedMatrics, setSelectedMatrics] = useState<string[]>([]);
  const [isBroadcasting, setIsBroadcasting] = useState(false);

  // Filtered and Sorted Voters for Email Broadcast
  const broadcastVoters = useMemo(() => {
    let result = voters.filter((v) => {
      const q = broadcastSearch.toLowerCase();
      const matchText = v.matric.toLowerCase().includes(q) || v.name.toLowerCase().includes(q) || v.email.toLowerCase().includes(q);
      if (!matchText) return false;

      const sentCount = v.email_sent_count || 0;
      if (broadcastFilter === 'UNSENT') return sentCount === 0;
      if (broadcastFilter === 'SENT') return sentCount > 0;
      if (broadcastFilter === 'UNVOTED') return !v.has_voted;
      if (broadcastFilter === 'VOTED') return v.has_voted;
      return true;
    });

    result.sort((a, b) => {
      const aSent = a.email_sent_count || 0;
      const bSent = b.email_sent_count || 0;
      if (broadcastSort === 'SENT_ASC') return aSent - bSent;
      if (broadcastSort === 'SENT_DESC') return bSent - aSent;
      if (broadcastSort === 'NAME') return a.name.localeCompare(b.name);
      if (broadcastSort === 'MATRIC') return a.matric.localeCompare(b.matric);
      return 0;
    });

    return result;
  }, [voters, broadcastSearch, broadcastFilter, broadcastSort]);

  const handleSelectAllBroadcast = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedMatrics(broadcastVoters.map((v) => v.matric));
    } else {
      setSelectedMatrics([]);
    }
  };

  const handleToggleSelectVoter = (matric: string) => {
    setSelectedMatrics((prev) =>
      prev.includes(matric) ? prev.filter((m) => m !== matric) : [...prev, matric]
    );
  };

  // Dispatch Campaign
  const handleTriggerBroadcast = async (targetMode: 'ALL' | 'SELECTED' | 'UNSENT_ONLY' | 'UNVOTED_ONLY') => {
    let targetMatrics: string[] = [];

    if (targetMode === 'ALL') {
      targetMatrics = voters.map((v) => v.matric);
    } else if (targetMode === 'SELECTED') {
      targetMatrics = selectedMatrics;
    } else if (targetMode === 'UNSENT_ONLY') {
      targetMatrics = voters.filter((v) => (v.email_sent_count || 0) === 0).map((v) => v.matric);
    } else if (targetMode === 'UNVOTED_ONLY') {
      targetMatrics = voters.filter((v) => !v.has_voted).map((v) => v.matric);
    }

    if (targetMatrics.length === 0) {
      alert('No recipients match the selected dispatch criteria.');
      return;
    }

    setIsBroadcasting(true);
    const res = await api.sendTargetedBroadcast(
      election.id,
      targetMatrics,
      broadcastTemplate,
      customSubject,
      customBody,
      election.title
    );

    if (res.success) {
      showToast(res.message);
      setLastBroadcastSummary(res.message + (res.rotationSummary ? ` [${res.rotationSummary}]` : ''));
      const updated = await api.getVoters(election.id);
      setVoters(updated);
      setSelectedMatrics([]);
      setEmailSettings(api.getEmailSettings(election.title));
      setAuditLogs(api.getAuditLogs());
    } else {
      alert('Broadcast dispatch error.');
    }
    setIsBroadcasting(false);
  };

  // Individual Resend Email
  const handleIndividualResend = async (matric: string) => {
    const res = await api.sendTargetedBroadcast(election.id, [matric], broadcastTemplate, undefined, undefined, election.title);
    if (res.success) {
      showToast(`Dispatched email pass to ${matric}. Sent counter incremented.`);
      const updated = await api.getVoters(election.id);
      setVoters(updated);
      setEmailSettings(api.getEmailSettings(election.title));
    }
  };

  // ---------------------------------------------------------------------------
  // TAB 5: DEMOGRAPHIC ANALYTICS HANDLER
  // ---------------------------------------------------------------------------
  const handleExportAnalyticsCsv = () => {
    if (!analytics) return;
    let csv = 'Category,Cohort,Accredited,Voted,Turnout Percentage\n';
    analytics.byLevel.forEach((r) => {
      csv += `Level,"${r.group}",${r.accredited},${r.voted},${r.percentage}%\n`;
    });
    analytics.byGender.forEach((r) => {
      csv += `Gender,"${r.group}",${r.accredited},${r.voted},${r.percentage}%\n`;
    });
    analytics.byHall.forEach((r) => {
      csv += `Hall,"${r.group}",${r.accredited},${r.voted},${r.percentage}%\n`;
    });

    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
    link.download = `demographic-analytics-${election.slug || 'election'}.csv`;
    link.click();
  };

  // ---------------------------------------------------------------------------
  // TAB 6: ELECTION SETTINGS STATE & HANDLERS
  // ---------------------------------------------------------------------------
  const toDateTimeLocal = (isoString?: string): string => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  const [settingsTitle, setSettingsTitle] = useState(election.title);
  const [settingsSlug, setSettingsSlug] = useState(election.slug);
  const [settingsOrgName, setSettingsOrgName] = useState(election.organizationName || '');
  const [settingsDesc, setSettingsDesc] = useState(election.description);
  const [settingsSupportEmail, setSettingsSupportEmail] = useState(election.supportEmail || 'support@electpoll.com');
  const [settingsLogo1, setSettingsLogo1] = useState(election.logo1Url || '');
  const [settingsLogo2, setSettingsLogo2] = useState(election.logo2Url || '');
  const [uploadingLogo1, setUploadingLogo1] = useState(false);
  const [uploadingLogo2, setUploadingLogo2] = useState(false);

  // Signatory State
  const [settingsCommName, setSettingsCommName] = useState(election.commissionerName || '');
  const [settingsCommTitle, setSettingsCommTitle] = useState(election.commissionerTitle || 'Chief Electoral Commissioner / Returning Officer');
  const [settingsCommSig, setSettingsCommSig] = useState(election.commissionerSignatureUrl || '');
  const [uploadingCommSig, setUploadingCommSig] = useState(false);
  const [settingsObsName, setSettingsObsName] = useState(election.observerName || '');
  const [settingsObsTitle, setSettingsObsTitle] = useState(election.observerTitle || 'Faculty / Staff Electoral Observer');
  const [settingsObsSig, setSettingsObsSig] = useState(election.observerSignatureUrl || '');
  const [uploadingObsSig, setUploadingObsSig] = useState(false);
  const [settingsElectpollRepName, setSettingsElectpollRepName] = useState(election.electpollRepName || 'Bukola Olapade');
  const [settingsElectpollRepTitle, setSettingsElectpollRepTitle] = useState(election.electpollRepTitle || 'Electpoll Platform Representative & Systems Auditor');
  const [settingsElectpollRepSig, setSettingsElectpollRepSig] = useState(election.electpollRepSignatureUrl || '');
  const [uploadingElectpollRepSig, setUploadingElectpollRepSig] = useState(false);

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [liveResults, setLiveResults] = useState<LiveResultsResponse | null>(null);
  const [settingsStartTime, setSettingsStartTime] = useState(toDateTimeLocal(election.startTime));
  const [settingsEndTime, setSettingsEndTime] = useState(toDateTimeLocal(election.endTime));
  const [settingsAllowLive, setSettingsAllowLive] = useState(election.allowLiveResults ?? true);
  const [settingsProtectResults, setSettingsProtectResults] = useState(election.isResultsProtected ?? false);
  const [settingsResultsPassword, setSettingsResultsPassword] = useState(election.resultsPassword || '');
  const [savingSettings, setSavingSettings] = useState(false);

  // Site Metadata & SEO State
  const [settingsMetaTitle, setSettingsMetaTitle] = useState(election.metaTitle || '');
  const [settingsMetaDesc, setSettingsMetaDesc] = useState(election.metaDescription || '');
  const [settingsOgImage, setSettingsOgImage] = useState(election.ogImageUrl || '');
  const [settingsMetaKeywords, setSettingsMetaKeywords] = useState(election.metaKeywords || '');
  const [uploadingOgImage, setUploadingOgImage] = useState(false);

  // Demographic Visibility Toggles
  const [settingsShowDemographicsLevel, setSettingsShowDemographicsLevel] = useState(election.showDemographicsLevel ?? true);
  const [settingsShowDemographicsGender, setSettingsShowDemographicsGender] = useState(election.showDemographicsGender ?? true);
  const [settingsShowDemographicsHall, setSettingsShowDemographicsHall] = useState(election.showDemographicsHall ?? true);
  const [settingsShowDemographicsHourly, setSettingsShowDemographicsHourly] = useState(election.showDemographicsHourly ?? true);

  useEffect(() => {
    setSettingsTitle(election.title);
    setSettingsSlug(election.slug);
    setSettingsOrgName(election.organizationName || '');
    setSettingsDesc(election.description);
    setSettingsSupportEmail(election.supportEmail || 'support@electpoll.com');
    setSettingsLogo1(election.logo1Url || '');
    setSettingsLogo2(election.logo2Url || '');
    setSettingsCommName(election.commissionerName || '');
    setSettingsCommTitle(election.commissionerTitle || 'Chief Electoral Commissioner / Returning Officer');
    setSettingsCommSig(election.commissionerSignatureUrl || '');
    setSettingsObsName(election.observerName || '');
    setSettingsObsTitle(election.observerTitle || 'Faculty / Staff Electoral Observer');
    setSettingsObsSig(election.observerSignatureUrl || '');
    setSettingsElectpollRepName(election.electpollRepName || 'Bukola Olapade');
    setSettingsElectpollRepTitle(election.electpollRepTitle || 'Electpoll Platform Representative & Systems Auditor');
    setSettingsElectpollRepSig(election.electpollRepSignatureUrl || '');
    setSettingsStartTime(toDateTimeLocal(election.startTime));
    setSettingsEndTime(toDateTimeLocal(election.endTime));
    setSettingsAllowLive(election.allowLiveResults ?? true);
    setSettingsProtectResults(election.isResultsProtected ?? false);
    setSettingsResultsPassword(election.resultsPassword || '');
    setSettingsMetaTitle(election.metaTitle || '');
    setSettingsMetaDesc(election.metaDescription || '');
    setSettingsOgImage(election.ogImageUrl || '');
    setSettingsMetaKeywords(election.metaKeywords || '');
    setSettingsShowDemographicsLevel(election.showDemographicsLevel ?? true);
    setSettingsShowDemographicsGender(election.showDemographicsGender ?? true);
    setSettingsShowDemographicsHall(election.showDemographicsHall ?? true);
    setSettingsShowDemographicsHourly(election.showDemographicsHourly ?? true);
  }, [election]);

  // Upload Social Share Preview Image to Cloudinary
  const handleOgImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingOgImage(true);
    const res = await api.uploadImageToCloudinary(file);
    if (res.success && res.url) {
      setSettingsOgImage(res.url);
      setElection((prev) => ({ ...prev, ogImageUrl: res.url }));
      showToast('Social share preview image uploaded successfully to Cloudinary');
    } else {
      const localUrl = URL.createObjectURL(file);
      setSettingsOgImage(localUrl);
      setElection((prev) => ({ ...prev, ogImageUrl: localUrl }));
      showToast('Using local image preview for social share image');
    }
    setUploadingOgImage(false);
  };

  // Upload Logo 1 to Cloudinary
  const handleLogo1Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo1(true);
    const res = await api.uploadImageToCloudinary(file);
    if (res.success && res.url) {
      setSettingsLogo1(res.url);
      showToast('Logo 1 uploaded successfully to Cloudinary');
    } else {
      const localUrl = URL.createObjectURL(file);
      setSettingsLogo1(localUrl);
      showToast('Using local image preview for Logo 1');
    }
    setUploadingLogo1(false);
  };

  // Upload Logo 2 to Cloudinary
  const handleLogo2Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo2(true);
    const res = await api.uploadImageToCloudinary(file);
    if (res.success && res.url) {
      setSettingsLogo2(res.url);
      showToast('Logo 2 uploaded successfully to Cloudinary');
    } else {
      const localUrl = URL.createObjectURL(file);
      setSettingsLogo2(localUrl);
      showToast('Using local image preview for Logo 2');
    }
    setUploadingLogo2(false);
  };

  // Upload Commissioner Signature to Cloudinary
  const handleCommSigUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCommSig(true);
    const res = await api.uploadImageToCloudinary(file);
    if (res.success && res.url) {
      setSettingsCommSig(res.url);
      setElection((prev) => ({ ...prev, commissionerSignatureUrl: res.url }));
      showToast('Commissioner signature uploaded successfully to Cloudinary');
    } else {
      const localUrl = URL.createObjectURL(file);
      setSettingsCommSig(localUrl);
      setElection((prev) => ({ ...prev, commissionerSignatureUrl: localUrl }));
      showToast('Using local image preview for signature');
    }
    setUploadingCommSig(false);
  };

  // Upload Observer Signature to Cloudinary
  const handleObsSigUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingObsSig(true);
    const res = await api.uploadImageToCloudinary(file);
    if (res.success && res.url) {
      setSettingsObsSig(res.url);
      setElection((prev) => ({ ...prev, observerSignatureUrl: res.url }));
      showToast('Observer signature uploaded successfully to Cloudinary');
    } else {
      const localUrl = URL.createObjectURL(file);
      setSettingsObsSig(localUrl);
      setElection((prev) => ({ ...prev, observerSignatureUrl: localUrl }));
      showToast('Using local image preview for signature');
    }
    setUploadingObsSig(false);
  };

  // Upload Electpoll Representative Signature to Cloudinary
  const handleElectpollRepSigUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingElectpollRepSig(true);
    const res = await api.uploadImageToCloudinary(file);
    if (res.success && res.url) {
      setSettingsElectpollRepSig(res.url);
      setElection((prev) => ({ ...prev, electpollRepSignatureUrl: res.url }));
      showToast('Electpoll Representative signature uploaded successfully to Cloudinary');
    } else {
      const localUrl = URL.createObjectURL(file);
      setSettingsElectpollRepSig(localUrl);
      setElection((prev) => ({ ...prev, electpollRepSignatureUrl: localUrl }));
      showToast('Using local image preview for signature');
    }
    setUploadingElectpollRepSig(false);
  };

  // Open PDF modal and preload live results
  const openPdfExportModal = async () => {
    try {
      const res = await api.getLiveResults(election.resultsPassword);
      setLiveResults(res);
    } catch {
      // Handled internally in modal fallback
    }
    setPdfModalOpen(true);
  };

  // Quick logo update callback from within PDF modal
  const handleUpdateElectionLogos = async (logo1: string, logo2: string) => {
    await api.updateElectionSettings(election.id, { logo1Url: logo1, logo2Url: logo2 });
    setElection((prev) => ({ ...prev, logo1Url: logo1, logo2Url: logo2 }));
    setSettingsLogo1(logo1);
    setSettingsLogo2(logo2);
    showToast('Official co-branding logos updated successfully');
  };

  // Quick signature update callback from within PDF modal
  const handleUpdateElectionSignatures = async (signatures: {
    commissionerName?: string;
    commissionerTitle?: string;
    commissionerSignatureUrl?: string;
    observerName?: string;
    observerTitle?: string;
    observerSignatureUrl?: string;
    electpollRepName?: string;
    electpollRepTitle?: string;
    electpollRepSignatureUrl?: string;
  }) => {
    await api.updateElectionSettings(election.id, signatures);
    setElection((prev) => ({ ...prev, ...signatures }));
    if (signatures.commissionerName !== undefined) setSettingsCommName(signatures.commissionerName);
    if (signatures.commissionerTitle !== undefined) setSettingsCommTitle(signatures.commissionerTitle);
    if (signatures.commissionerSignatureUrl !== undefined) setSettingsCommSig(signatures.commissionerSignatureUrl);
    if (signatures.observerName !== undefined) setSettingsObsName(signatures.observerName);
    if (signatures.observerTitle !== undefined) setSettingsObsTitle(signatures.observerTitle);
    if (signatures.observerSignatureUrl !== undefined) setSettingsObsSig(signatures.observerSignatureUrl);
    if (signatures.electpollRepName !== undefined) setSettingsElectpollRepName(signatures.electpollRepName);
    if (signatures.electpollRepTitle !== undefined) setSettingsElectpollRepTitle(signatures.electpollRepTitle);
    if (signatures.electpollRepSignatureUrl !== undefined) setSettingsElectpollRepSig(signatures.electpollRepSignatureUrl);
    showToast('Official signatories and signatures updated successfully');
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    const updatedFields: Partial<ElectionSummary> = {
      title: settingsTitle.trim(),
      slug: settingsSlug.trim().toLowerCase().replace(/[^a-z0-9-_]/g, '-'),
      organizationName: settingsOrgName.trim(),
      description: settingsDesc.trim(),
      supportEmail: settingsSupportEmail.trim(),
      logo1Url: settingsLogo1.trim(),
      logo2Url: settingsLogo2.trim(),
      commissionerName: settingsCommName.trim(),
      commissionerTitle: settingsCommTitle.trim(),
      commissionerSignatureUrl: settingsCommSig.trim(),
      observerName: settingsObsName.trim(),
      observerTitle: settingsObsTitle.trim(),
      observerSignatureUrl: settingsObsSig.trim(),
      electpollRepName: settingsElectpollRepName.trim(),
      electpollRepTitle: settingsElectpollRepTitle.trim(),
      electpollRepSignatureUrl: settingsElectpollRepSig.trim(),
      startTime: settingsStartTime ? new Date(settingsStartTime).toISOString() : '',
      endTime: settingsEndTime ? new Date(settingsEndTime).toISOString() : '',
      allowLiveResults: settingsAllowLive,
      isResultsProtected: settingsProtectResults,
      resultsPassword: settingsProtectResults ? settingsResultsPassword.trim() : '',
      metaTitle: settingsMetaTitle.trim(),
      metaDescription: settingsMetaDesc.trim(),
      ogImageUrl: settingsOgImage.trim(),
      metaKeywords: settingsMetaKeywords.trim(),
      showDemographicsLevel: settingsShowDemographicsLevel,
      showDemographicsGender: settingsShowDemographicsGender,
      showDemographicsHall: settingsShowDemographicsHall,
      showDemographicsHourly: settingsShowDemographicsHourly,
    };

    await api.updateElectionSettings(election.id, updatedFields);
    const merged = { ...election, ...updatedFields };
    setElection(merged);
    setSiteMeta({ election: merged, pageTitle: 'Admin Dashboard' });
    showToast('Election settings updated! Metadata, schedule, branding, and signatures are now live.');
    setSavingSettings(false);
  };

  const handlePurgeVotes = async () => {
    const confirmation = window.prompt(
      'WARNING: This will reset all submitted ballots and set all voters back to NOT VOTED. Type "RESET" to confirm:'
    );
    if (confirmation === 'RESET') {
      await api.resetElectionVotes(election.id);
      showToast('Ballot box purged. All voter statuses reset to NOT VOTED.');
      refreshAllData();
    }
  };

  if (!admin) return null;

  return (
    <div className="admin-dashboard-layout">
      {/* Persistent Left Sidebar */}
      <AdminSidebar
        currentTab={currentTab}
        onTabChange={setTab}
        voterCount={totalAccredited}
        electionStatus={election.status}
      />

      {/* Main Administrative Workbench */}
      <main className="admin-main-content">
        {/* Floating Toast Feedback Notification */}
        {toastMessage && (
          <div
            style={{
              position: 'fixed',
              top: '24px',
              right: '28px',
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              padding: '12px 20px',
              borderRadius: '8px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '13.5px',
              fontWeight: 600,
              zIndex: 1000,
              animation: 'fadeIn 0.2s ease',
            }}
          >
            <CheckCircle2 size={18} />
            <span>{toastMessage}</span>
          </div>
        )}

        {/* Global Page Header */}
        <header
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: '24px',
            borderBottom: '1px solid #222126',
            marginBottom: '32px',
            gap: '16px',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                {election.organizationName || 'Election Setup & Administration'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '6px', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '26px', fontWeight: 800, margin: 0, letterSpacing: '-0.5px' }}>
                {election.title}
              </h1>

              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: '16px',
                  backgroundColor:
                    election.status === 'active'
                      ? 'rgba(16, 185, 129, 0.15)'
                      : election.status === 'paused'
                      ? 'rgba(245, 158, 11, 0.15)'
                      : 'rgba(107, 114, 128, 0.2)',
                  color:
                    election.status === 'active'
                      ? '#10B981'
                      : election.status === 'paused'
                      ? '#F59E0B'
                      : '#9CA3AF',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor:
                      election.status === 'active'
                        ? '#10B981'
                        : election.status === 'paused'
                        ? '#F59E0B'
                        : '#9CA3AF',
                  }}
                />
                {election.status}
              </span>
            </div>
          </div>

          {/* Quick Election State Switcher */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => handleStatusChange('active')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: election.status === 'active' ? 'rgba(16, 185, 129, 0.2)' : 'transparent',
                borderColor: '#10B981',
                borderWidth: '1px',
                borderStyle: 'solid',
                color: '#10B981',
                cursor: 'pointer',
              }}
            >
              <Play size={13} /> Resume Live
            </button>
            <button
              onClick={() => handleStatusChange('paused')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: election.status === 'paused' ? 'rgba(245, 158, 11, 0.2)' : 'transparent',
                borderColor: '#F59E0B',
                borderWidth: '1px',
                borderStyle: 'solid',
                color: '#F59E0B',
                cursor: 'pointer',
              }}
            >
              <Pause size={13} /> Pause Voting
            </button>
            <button
              onClick={() => handleStatusChange('ended')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: election.status === 'ended' ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                borderColor: '#EF4444',
                borderWidth: '1px',
                borderStyle: 'solid',
                color: '#EF4444',
                cursor: 'pointer',
              }}
            >
              <StopCircle size={13} /> End Election
            </button>
          </div>
        </header>

        {/* Global KPI Metrics Header Bar */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: '16px',
            marginBottom: '32px',
          }}
        >
          <div style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
            <div style={{ color: '#8E8E93', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Turnout Rate
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#FF9900', marginTop: '6px' }}>
              {turnoutPercent}%
            </div>
            <div style={{ fontSize: '11px', color: '#10B981', marginTop: '4px' }}>
              {totalVoted} of {totalAccredited} participated
            </div>
          </div>

          <div style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
            <div style={{ color: '#8E8E93', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Accredited Voters
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#FFFFFF', marginTop: '6px' }}>
              {totalAccredited}
            </div>
            <div style={{ fontSize: '11px', color: '#8E8E93', marginTop: '4px' }}>
              Enrolled students on registry
            </div>
          </div>

          <div style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
            <div style={{ color: '#8E8E93', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Ballots Cast
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#10B981', marginTop: '6px' }}>
              {totalVoted}
            </div>
            <div style={{ fontSize: '11px', color: '#8E8E93', marginTop: '4px' }}>
              Zero duplicate rejections
            </div>
          </div>

          <div style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
            <div style={{ color: '#8E8E93', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Email Dispatches
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#38BDF8', marginTop: '6px' }}>
              {totalEmailsSent}
            </div>
            <div style={{ fontSize: '11px', color: '#8E8E93', marginTop: '4px' }}>
              Credentials & reminders sent
            </div>
          </div>
        </section>

        {/* =================================================================== */}
        {/* PAGE 1: OVERVIEW                                                    */}
        {/* =================================================================== */}
        {currentTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            {/* Main Welcome Card */}
            <div style={{ backgroundColor: '#171619', padding: '28px 32px', borderRadius: '16px', border: '1px solid #2D2C31' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                <div style={{ maxWidth: '680px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Executive Overview
                  </span>
                  <h2 style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', marginBottom: '8px' }}>
                    {election.title} Command Center
                  </h2>
                  <p style={{ color: '#A1A1AA', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                    {election.description}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setTab('voters')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 18px',
                      borderRadius: '8px',
                      backgroundColor: '#FF9900',
                      color: '#171619',
                      border: 'none',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    <Users size={16} /> Manage Voters
                  </button>
                  <button
                    onClick={() => setTab('candidates')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 18px',
                      borderRadius: '8px',
                      backgroundColor: '#2D2C31',
                      color: '#FFFFFF',
                      border: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    <Award size={16} /> Candidate Roles
                  </button>
                  <button
                    onClick={openPdfExportModal}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 18px',
                      borderRadius: '8px',
                      backgroundColor: '#1E1D21',
                      border: '1px solid #FF990080',
                      color: '#FF9900',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <FileSpreadsheet size={16} /> Official PDF Report
                  </button>
                </div>
              </div>

              {/* Security & Live Results Gate Status */}
              <div
                style={{
                  backgroundColor: '#1E1D21',
                  borderRadius: '12px',
                  border: '1px solid #2D2C31',
                  padding: '20px 24px',
                  marginTop: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '16px',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>
                      Results Access Security Gate
                    </h3>
                    {election.isResultsProtected ? (
                      <span style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#EF4444', fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Lock size={12} /> Password Protected
                      </span>
                    ) : (
                      <span style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10B981', fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Unlock size={12} /> Public (Unpassworded)
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#8E8E93', fontSize: '13px', marginTop: '6px', margin: 0 }}>
                    {election.isResultsProtected
                      ? `Access code configured ("${election.resultsPassword || 'Configured'}"). Only voters with this password can view live tallies.`
                      : 'Live tallies and vote charts are accessible publicly.'}
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <a
                    href="/results"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #3F3F46',
                      color: '#E4E4E7',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      textDecoration: 'none',
                    }}
                  >
                    <ExternalLink size={13} /> View Results Page
                  </a>

                  {election.isResultsProtected ? (
                    <button
                      onClick={() => handleSaveResultsProtection(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        borderRadius: '6px',
                        border: '1px solid #10B981',
                        backgroundColor: 'transparent',
                        color: '#10B981',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Unlock size={13} /> Make Public
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setResultsPasswordInput('');
                        setPasswordModalOpen(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#FF9900',
                        color: '#171619',
                        border: 'none',
                        fontSize: '12.5px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      <Lock size={13} /> Password Protect Results
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Navigation Cards Grid (6 Modules) */}
            <div>
              <h3 style={{ fontSize: '17px', fontWeight: 700, marginBottom: '16px', color: '#FFFFFF' }}>
                Administrative Modules
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                <div
                  onClick={() => setTab('voters')}
                  style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31', cursor: 'pointer', transition: 'border-color 0.2s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(255, 153, 0, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Users size={18} color="#FF9900" />
                    </div>
                    <h4 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Accredited Voters</h4>
                  </div>
                  <p style={{ color: '#8E8E93', fontSize: '13px', lineHeight: 1.5, margin: 0 }}>
                    Manage authorized matric numbers, demographic cohorts, and import rosters via CSV.
                  </p>
                </div>

                <div
                  onClick={() => setTab('candidates')}
                  style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31', cursor: 'pointer', transition: 'border-color 0.2s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(56, 189, 248, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Award size={18} color="#38BDF8" />
                    </div>
                    <h4 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Candidate Details</h4>
                  </div>
                  <p style={{ color: '#8E8E93', fontSize: '13px', lineHeight: 1.5, margin: 0 }}>
                    Set up roles, candidate portraits, bios, and configure level eligibility rules defining who can vote.
                  </p>
                </div>

                <div
                  onClick={() => setTab('broadcast')}
                  style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31', cursor: 'pointer', transition: 'border-color 0.2s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Mail size={18} color="#10B981" />
                    </div>
                    <h4 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Emails Broadcast</h4>
                  </div>
                  <p style={{ color: '#8E8E93', fontSize: '13px', lineHeight: 1.5, margin: 0 }}>
                    Dispatch voter credentials, live alerts, sort by send count, and resend emails with tracked counters.
                  </p>
                </div>

                <div
                  onClick={() => setTab('analytics')}
                  style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31', cursor: 'pointer', transition: 'border-color 0.2s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(168, 85, 247, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <BarChart3 size={18} color="#A855F7" />
                    </div>
                    <h4 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Demographic Analysis</h4>
                  </div>
                  <p style={{ color: '#8E8E93', fontSize: '13px', lineHeight: 1.5, margin: 0 }}>
                    Interactive turnout intelligence segmented by academic level, gender, and student residence halls.
                  </p>
                </div>

                <div
                  onClick={() => setTab('settings')}
                  style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '12px', border: '1px solid #2D2C31', cursor: 'pointer', transition: 'border-color 0.2s' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(245, 158, 11, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Settings size={18} color="#F59E0B" />
                    </div>
                    <h4 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Election Settings</h4>
                  </div>
                  <p style={{ color: '#8E8E93', fontSize: '13px', lineHeight: 1.5, margin: 0 }}>
                    Customize the election title, branding, schedules, and platform-wide configurations.
                  </p>
                </div>
              </div>
            </div>

            {/* Audit Log Stream */}
            <div style={{ backgroundColor: '#171619', padding: '24px 28px', borderRadius: '16px', border: '1px solid #2D2C31' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Recent Election Activity Log</h3>
                <span style={{ fontSize: '12px', color: '#8E8E93' }}>Cryptographic Edge Trail</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {auditLogs.slice(0, 5).map((log) => (
                  <div
                    key={log.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      backgroundColor: '#1E1D21',
                      borderRadius: '8px',
                      border: '1px solid #2D2C31',
                      fontSize: '13px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#FF9900', fontWeight: 700 }}>[{log.actor}]</span>
                      <span style={{ fontWeight: 600 }}>{log.action}</span>
                    </div>
                    <span style={{ color: '#8E8E93', fontSize: '12px' }}>
                      {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* =================================================================== */}
        {/* PAGE 2: ACCREDITED VOTERS / VOTERS REGISTRY                         */}
        {/* =================================================================== */}
        {currentTab === 'voters' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h2 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Voter Accreditation Registry</h2>
                <p style={{ color: '#8E8E93', fontSize: '13px', marginTop: '4px', margin: 0 }}>
                  Manage accredited student records, filter by academic levels, and import student voter lists.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setNewVoterPassword(generateVoterPassword()); setAddVoterModalOpen(true); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '9px 14px',
                    borderRadius: '6px',
                    border: '1px solid #3F3F46',
                    backgroundColor: '#1E1D21',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <PlusCircle size={15} color="#FF9900" /> Add Single Voter
                </button>

                <button
                  onClick={() => setCsvModalOpen(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '9px 16px',
                    borderRadius: '6px',
                    backgroundColor: '#FF9900',
                    color: '#171619',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  <FileSpreadsheet size={15} /> Import Voters CSV
                </button>

                {recentBatchVoters && recentBatchVoters.length > 0 && (
                  <button
                    onClick={() => handleExportRecentBatchCsv()}
                    title="Export credentials for only the newly added/updated batch"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '9px 14px',
                      borderRadius: '6px',
                      border: '1px solid #FF9900',
                      backgroundColor: 'rgba(255, 153, 0, 0.12)',
                      color: '#FF9900',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Download size={15} /> Export Recent Batch ({recentBatchVoters.length})
                  </button>
                )}

                <button
                  onClick={() => setExportModalOpen(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '9px 14px',
                    borderRadius: '6px',
                    border: '1px solid #10B981',
                    backgroundColor: 'transparent',
                    color: '#10B981',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Download size={15} /> Export Roster
                </button>

                <button
                  onClick={handleDeleteAllVoters}
                  disabled={voters.length === 0}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '9px 14px',
                    borderRadius: '6px',
                    border: '1px solid #EF4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.12)',
                    color: '#EF4444',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: voters.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: voters.length === 0 ? 0.5 : 1,
                    transition: 'all 0.15s ease',
                  }}
                  title="Permanently remove all accredited voters"
                >
                  <Trash2 size={15} /> Delete All
                </button>
              </div>
            </div>

            {/* Filters & Search Toolbar */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                flexWrap: 'wrap',
                backgroundColor: '#171619',
                padding: '16px 20px',
                borderRadius: '12px',
                border: '1px solid #2D2C31',
              }}
            >
              <div style={{ position: 'relative', flex: 1, minWidth: '240px' }}>
                <Search size={16} color="#8E8E93" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Search matric, name, or email..."
                  value={voterSearch}
                  onChange={(e) => setVoterSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '9px 12px 9px 36px',
                    borderRadius: '6px',
                    backgroundColor: '#0E0D0F',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                />
              </div>

              {/* Level Filter */}
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                style={{
                  padding: '9px 12px',
                  borderRadius: '6px',
                  backgroundColor: '#0E0D0F',
                  border: '1px solid #2D2C31',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <option value="ALL">All Levels</option>
                <option value="100">100 Level</option>
                <option value="200">200 Level</option>
                <option value="300">300 Level</option>
                <option value="400">400 Level</option>
                <option value="500">500 Level</option>
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '9px 12px',
                  borderRadius: '6px',
                  backgroundColor: '#0E0D0F',
                  border: '1px solid #2D2C31',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <option value="ALL">All Voting Statuses</option>
                <option value="VOTED">VOTED</option>
                <option value="NOT_VOTED">NOT VOTED</option>
              </select>

              {/* Gender Filter */}
              <select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                style={{
                  padding: '9px 12px',
                  borderRadius: '6px',
                  backgroundColor: '#0E0D0F',
                  border: '1px solid #2D2C31',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <option value="ALL">All Genders</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>

              <span style={{ fontSize: '12px', color: '#8E8E93', marginLeft: 'auto' }}>
                Showing {filteredVoters.length} of {voters.length} voters
              </span>
            </div>

            {/* Voter Table */}
            <div style={{ backgroundColor: '#171619', borderRadius: '12px', border: '1px solid #2D2C31', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2D2C31', backgroundColor: '#1E1D21', color: '#8E8E93', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <th style={{ padding: '14px 18px' }}>Matric No</th>
                      <th style={{ padding: '14px 18px' }}>Student Name</th>
                      <th style={{ padding: '14px 18px' }}>Email</th>
                      <th style={{ padding: '14px 18px' }}>Password</th>
                      <th style={{ padding: '14px 18px' }}>Level</th>
                      <th style={{ padding: '14px 18px' }}>Gender</th>
                      <th style={{ padding: '14px 18px' }}>Hall</th>
                      <th style={{ padding: '14px 18px' }}>Vote Status</th>
                      <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVoters.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ padding: '36px', textAlign: 'center', color: '#8E8E93' }}>
                          No voters matching the search and filter criteria.
                        </td>
                      </tr>
                    ) : (
                      filteredVoters.map((voter) => (
                        <tr key={voter.matric} style={{ borderBottom: '1px solid #1E1D21' }}>
                          <td style={{ padding: '12px 18px', fontWeight: 700, color: '#FF9900' }}>{voter.matric}</td>
                          <td style={{ padding: '12px 18px', fontWeight: 600 }}>{voter.name}</td>
                          <td style={{ padding: '12px 18px', color: '#A1A1AA', fontSize: '12.5px' }}>{voter.email}</td>
                          <td style={{ padding: '12px 18px' }}>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#232227',
                                border: '1px solid #3A3940',
                                borderRadius: '6px',
                                padding: '3px 8px',
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#F4F4F5',
                                  letterSpacing: '0.5px',
                                }}
                              >
                                {voter.password || 'pass123'}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(voter.password || 'pass123');
                                  showToast(`Copied password for ${voter.matric}`);
                                }}
                                title="Copy password"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#A1A1AA',
                                  cursor: 'pointer',
                                  padding: '2px',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Copy size={12} />
                              </button>
                            </div>
                          </td>
                          <td style={{ padding: '12px 18px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: '#2D2C31', fontSize: '11px', fontWeight: 700 }}>
                              {voter.level}L
                            </span>
                          </td>
                          <td style={{ padding: '12px 18px', color: '#9CA3AF' }}>{voter.gender || 'Unspecified'}</td>
                          <td style={{ padding: '12px 18px', color: '#9CA3AF' }}>{voter.hall || 'Independence Hall'}</td>
                          <td style={{ padding: '12px 18px' }}>
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '3px 8px',
                                borderRadius: '12px',
                                backgroundColor: voter.has_voted ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                color: voter.has_voted ? '#10B981' : '#EF4444',
                              }}
                            >
                              {voter.has_voted ? 'VOTED' : 'NOT VOTED'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={() => handleOpenEditVoter(voter)}
                                title="Edit voter details"
                                style={{
                                  background: 'rgba(255, 153, 0, 0.1)',
                                  border: '1px solid rgba(255, 153, 0, 0.25)',
                                  borderRadius: '6px',
                                  color: '#FF9900',
                                  cursor: 'pointer',
                                  padding: '5px 7px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteVoter(voter.matric)}
                                title="Delete voter"
                                style={{
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  border: '1px solid rgba(239, 68, 68, 0.25)',
                                  borderRadius: '6px',
                                  color: '#EF4444',
                                  cursor: 'pointer',
                                  padding: '5px 7px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* =================================================================== */}
        {/* PAGE 3: CANDIDATE DETAILS                                           */}
        {/* =================================================================== */}
        {currentTab === 'candidates' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h2 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Candidate & Role Administration</h2>
                <p style={{ color: '#8E8E93', fontSize: '13px', marginTop: '4px', margin: 0 }}>
                  Configure candidate profiles, photos, and define role eligibility specifying who can vote for each position.
                </p>
              </div>

              <button
                onClick={() => openRoleModal()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '9px 16px',
                  borderRadius: '6px',
                  backgroundColor: '#FF9900',
                  color: '#171619',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                <PlusCircle size={15} /> Add New Role / Position
              </button>
            </div>

            {/* List of Roles and Candidates */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {Object.keys(rolesData).length === 0 ? (
                <div style={{ backgroundColor: '#171619', padding: '48px', borderRadius: '12px', textAlign: 'center', border: '1px solid #2D2C31', color: '#8E8E93' }}>
                  No roles configured yet. Click "Add New Role / Position" to set up offices.
                </div>
              ) : (
                Object.entries(rolesData).map(([roleKey, pos], roleIdx, allRoles) => {
                  const isRoleBeingDragged = draggingRoleIndex === roleIdx;
                  const isRoleDragTarget = dragOverRoleIndex === roleIdx && !isRoleBeingDragged;

                  return (
                    <div
                      key={roleKey}
                      draggable={true}
                      onDragStart={(e) => handleRoleDragStart(e, roleIdx)}
                      onDragOver={(e) => handleRoleDragOver(e, roleIdx)}
                      onDrop={(e) => handleRoleDrop(e, roleIdx)}
                      onDragEnd={handleRoleDragEnd}
                      style={{
                        backgroundColor: '#171619',
                        borderRadius: '14px',
                        border: isRoleDragTarget
                          ? '2px dashed #FF9900'
                          : isRoleBeingDragged
                          ? '1px dashed #52525B'
                          : '1px solid #2D2C31',
                        boxShadow: isRoleDragTarget
                          ? '0 0 20px rgba(255, 153, 0, 0.25)'
                          : 'none',
                        opacity: isRoleBeingDragged ? 0.35 : 1,
                        padding: '24px',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {/* Role Ordering Header Bar */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '10px',
                          paddingBottom: '14px',
                          borderBottom: '1px solid #222126',
                          marginBottom: '16px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'grab',
                              color: '#9CA3AF',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #2D2C31',
                              userSelect: 'none',
                            }}
                            title="Drag to rearrange role order on voter ballot"
                          >
                            <GripVertical size={14} color="#FF9900" />
                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#FF9900', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                              Ballot Position #{roleIdx + 1}
                            </span>
                          </div>
                          <span style={{ fontSize: '12px', color: '#71717A' }}>
                            (Drag card or use arrows to rearrange)
                          </span>
                        </div>

                        {/* One-touch Up / Down Nudge Buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveRole(roleIdx, 'up');
                            }}
                            disabled={roleIdx === 0}
                            title="Move role higher on ballot"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3F3F46',
                              color: roleIdx === 0 ? '#4B5563' : '#D1D5DB',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              cursor: roleIdx === 0 ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: 600,
                              opacity: roleIdx === 0 ? 0.4 : 1,
                            }}
                          >
                            <ArrowUp size={12} /> Move Up
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveRole(roleIdx, 'down');
                            }}
                            disabled={roleIdx === allRoles.length - 1}
                            title="Move role lower on ballot"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3F3F46',
                              color: roleIdx === allRoles.length - 1 ? '#4B5563' : '#D1D5DB',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              cursor: roleIdx === allRoles.length - 1 ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: 600,
                              opacity: roleIdx === allRoles.length - 1 ? 0.4 : 1,
                            }}
                          >
                            <ArrowDown size={12} /> Move Down
                          </button>
                        </div>
                      </div>
                    {/* Role Header */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: '12px',
                        paddingBottom: '18px',
                        borderBottom: '1px solid #222126',
                        marginBottom: '20px',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <h3 style={{ fontSize: '19px', fontWeight: 700, margin: 0 }}>{pos.name}</h3>
                          <span style={{ fontSize: '11px', color: '#8E8E93', backgroundColor: '#2D2C31', padding: '2px 8px', borderRadius: '4px' }}>
                            Key: {roleKey}
                          </span>
                          <span style={{ fontSize: '11px', color: '#FF9900', backgroundColor: 'rgba(255, 153, 0, 0.1)', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                            Max votes: {pos.max || 1}
                          </span>
                        </div>

                        {/* Who Can Vote Badges */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '12px', color: '#A1A1AA', fontWeight: 600 }}>Who Can Vote:</span>
                          {!pos.levels || pos.levels.length === 0 || pos.levels.length >= 5 ? (
                            <span style={{ fontSize: '11px', color: '#10B981', backgroundColor: 'rgba(16, 185, 129, 0.15)', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                              All Academic Levels
                            </span>
                          ) : (
                            pos.levels.map((lvl) => (
                              <span
                                key={lvl}
                                style={{
                                  fontSize: '11px',
                                  color: '#38BDF8',
                                  backgroundColor: 'rgba(56, 189, 248, 0.15)',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontWeight: 700,
                                }}
                              >
                                {lvl}L Only
                              </span>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Action buttons for Role */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => openCandidateModal(roleKey)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '7px 12px',
                            borderRadius: '6px',
                            backgroundColor: '#2D2C31',
                            color: '#FFFFFF',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          <PlusCircle size={13} color="#FF9900" /> Add Candidate
                        </button>
                        <button
                          onClick={() => openRoleModal(roleKey)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '7px 12px',
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                            border: '1px solid #3F3F46',
                            color: '#D1D5DB',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          <Edit size={13} /> Edit Role
                        </button>
                        <button
                          onClick={() => handleDeleteRole(roleKey)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '7px 10px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            color: '#EF4444',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    {/* Candidate Cards Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
                      {pos.candidates.map((cand, cIdx) => {
                        const isBeingDragged = draggingCand?.roleKey === roleKey && draggingCand?.index === cIdx;
                        const isDragTarget = dragOverCand?.roleKey === roleKey && dragOverCand?.index === cIdx && !isBeingDragged;
                        const isDisq = Boolean(cand.isDisqualified);

                        return (
                          <div
                            key={cand.id}
                            draggable={true}
                            onDragStart={(e) => handleCandidateDragStart(e, roleKey, cIdx)}
                            onDragOver={(e) => handleCandidateDragOver(e, roleKey, cIdx)}
                            onDrop={(e) => handleCandidateDrop(e, roleKey, cIdx)}
                            onDragEnd={handleCandidateDragEnd}
                            style={{
                              backgroundColor: isDisq ? '#221316' : isBeingDragged ? '#141316' : '#1E1D21',
                              borderRadius: '12px',
                              border: isDragTarget
                                ? '2px dashed #FF9900'
                                : isBeingDragged
                                ? '1.5px dashed #4B5563'
                                : isDisq
                                ? '1.5px solid #EF4444'
                                : cand.isAbstain
                                ? '1px dashed #3F3F46'
                                : '1px solid #2D2C31',
                              padding: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              opacity: isBeingDragged ? 0.35 : 1,
                              cursor: 'grab',
                              transition: 'border 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
                              position: 'relative',
                              boxShadow: isDragTarget ? '0 0 16px rgba(255, 153, 0, 0.25)' : isDisq ? '0 0 12px rgba(239, 68, 68, 0.12)' : 'none',
                            }}
                          >
                            <div>
                              {/* Top Bar: Order Badge, Disqualification Pill, and Drag Handle */}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: 700,
                                      color: '#9CA3AF',
                                      backgroundColor: '#2D2C31',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      letterSpacing: '0.5px',
                                    }}
                                    title="Ballot Order Index"
                                  >
                                    #{cIdx + 1}
                                  </span>
                                  {isDisq && (
                                    <span
                                      style={{
                                        fontSize: '10px',
                                        fontWeight: 800,
                                        color: '#F87171',
                                        backgroundColor: '#7F1D1D44',
                                        border: '1px solid #EF4444',
                                        padding: '2px 6px',
                                        borderRadius: '5px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                      }}
                                    >
                                      <Ban size={10} strokeWidth={2.5} /> Disqualified
                                    </span>
                                  )}
                                </div>
                                <div
                                  title="Drag to rearrange candidate order"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#6B7280',
                                    cursor: 'grab',
                                    padding: '2px',
                                  }}
                                >
                                  <GripVertical size={16} />
                                </div>
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <img
                                  src={cand.image}
                                  alt={cand.name}
                                  onError={(e) => {
                                    (e.target as HTMLElement).setAttribute('src', '/assets/images/userdefault.png');
                                  }}
                                  style={{
                                    width: '52px',
                                    height: '52px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: isDisq ? '2px solid #EF4444' : '2px solid #FF9900',
                                    filter: isDisq ? 'grayscale(80%)' : 'none',
                                    flexShrink: 0,
                                  }}
                                />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <h4 style={{ fontSize: '14.5px', fontWeight: 700, margin: 0, color: isDisq ? '#F87171' : cand.isAbstain ? '#9CA3AF' : '#FFFFFF', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                    {cand.name}
                                  </h4>
                                  {cand.nickname && (
                                    <span style={{ fontSize: '11.5px', color: isDisq ? '#FCA5A5' : '#FF9900', fontWeight: 700, display: 'block', marginTop: '2px' }}>
                                      "{cand.nickname}"
                                    </span>
                                  )}
                                  {cand.isAbstain && (
                                    <span style={{ fontSize: '10px', display: 'block', color: '#6B7280', textTransform: 'uppercase', fontWeight: 700, marginTop: '2px' }}>
                                      Neutral Option
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Disqualification Reason banner */}
                              {isDisq && (
                                <div
                                  style={{
                                    margin: '0 0 10px 0',
                                    padding: '6px 8px',
                                    backgroundColor: '#7F1D1D22',
                                    border: '1px dashed #EF444466',
                                    borderRadius: '6px',
                                    color: '#FCA5A5',
                                    fontSize: '11px',
                                    lineHeight: '1.35',
                                  }}
                                >
                                  <span style={{ display: 'block', fontSize: '9px', textTransform: 'uppercase', fontWeight: 800, color: '#F87171', marginBottom: '2px' }}>
                                    Disqualification Notice:
                                  </span>
                                  {cand.disqualificationReason ? cand.disqualificationReason : 'Mid-election disqualification'}
                                </div>
                              )}

                              {cand.bio && (
                                <p style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.4, margin: '0 0 12px 0' }}>
                                  {cand.bio}
                                </p>
                              )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px solid #2D2C31', marginTop: '8px', flexWrap: 'wrap', gap: '6px' }}>
                              {/* One-touch Nudge Buttons */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveCandidate(roleKey, cIdx, 'prev');
                                  }}
                                  disabled={cIdx === 0}
                                  title="Move earlier on ballot"
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid #3F3F46',
                                    color: cIdx === 0 ? '#4B5563' : '#D1D5DB',
                                    borderRadius: '4px',
                                    padding: '4px 6px',
                                    cursor: cIdx === 0 ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '11px',
                                    opacity: cIdx === 0 ? 0.4 : 1,
                                  }}
                                >
                                  <ArrowLeft size={12} />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveCandidate(roleKey, cIdx, 'next');
                                  }}
                                  disabled={cIdx === pos.candidates.length - 1}
                                  title="Move later on ballot"
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid #3F3F46',
                                    color: cIdx === pos.candidates.length - 1 ? '#4B5563' : '#D1D5DB',
                                    borderRadius: '4px',
                                    padding: '4px 6px',
                                    cursor: cIdx === pos.candidates.length - 1 ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '11px',
                                    opacity: cIdx === pos.candidates.length - 1 ? 0.4 : 1,
                                  }}
                                >
                                  <ArrowRight size={12} />
                                </button>
                              </div>

                              {/* Action Buttons: Disqualify / Reinstate + Edit + Delete */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <button
                                  type="button"
                                  onClick={() => handleQuickToggleDisqualify(roleKey, cand)}
                                  style={{
                                    background: isDisq ? '#064E3B33' : '#7F1D1D22',
                                    border: isDisq ? '1px solid #059669' : '1px solid #DC262666',
                                    color: isDisq ? '#34D399' : '#F87171',
                                    cursor: 'pointer',
                                    padding: '3px 7px',
                                    borderRadius: '5px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                  title={isDisq ? 'Reinstate to active ballot' : 'Disqualify candidate mid-election'}
                                >
                                  {isDisq ? <ShieldCheck size={12} /> : <Ban size={12} />}
                                  {isDisq ? 'Reinstate' : 'Disqualify'}
                                </button>
                                <button
                                  onClick={() => openCandidateModal(roleKey, cand)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#9CA3AF',
                                    cursor: 'pointer',
                                    padding: '4px 6px',
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  <Edit size={13} /> Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteCandidate(roleKey, cand.id)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#EF4444',
                                    cursor: 'pointer',
                                    padding: '4px 6px',
                                    fontSize: '12px',
                                  }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
              )}
            </div>
          </div>
        )}

        {/* =================================================================== */}
        {/* PAGE 4: EMAILS BROADCAST                                            */}
        {/* =================================================================== */}
        {currentTab === 'broadcast' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Header with Settings and Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h2 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Voter Email Dispatch Hub</h2>
                <p style={{ color: '#8E8E93', fontSize: '13px', marginTop: '4px', margin: 0 }}>
                  Transmit credentials, voting reminders, track delivery quotas, and manage multi-account rotation.
                </p>

                {/* Quota & Account Pill Badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: 'rgba(255, 153, 0, 0.08)',
                      border: '1px solid rgba(255, 153, 0, 0.25)',
                      padding: '4px 12px',
                      borderRadius: '20px',
                    }}
                  >
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#10B981' }} />
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF9900' }}>
                      {emailSettings.accounts.filter((a) => a.isActive).length} Sender Account(s) Active
                    </span>
                    <span style={{ color: '#8E8E93', fontSize: '11px' }}>•</span>
                    <span style={{ fontSize: '11px', color: '#D1D5DB' }}>
                      {emailSettings.accounts.reduce((acc, a) => acc + (a.isActive ? a.dailyLimit - a.sentToday : 0), 0)} /{' '}
                      {emailSettings.accounts.reduce((acc, a) => acc + (a.isActive ? a.dailyLimit : 0), 0)} daily quota left
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsConfigModalOpen(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      backgroundColor: '#1E1D21',
                      border: '1px solid #2D2C31',
                      color: '#FFFFFF',
                      fontSize: '11px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    <Settings size={12} color="#FF9900" />
                    <span>⚙ Configure Gmail & Senders</span>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  disabled={isBroadcasting}
                  onClick={() => handleTriggerBroadcast('ALL')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '9px 16px',
                    borderRadius: '6px',
                    backgroundColor: '#FF9900',
                    color: '#171619',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  <Send size={15} /> Send to All ({voters.length})
                </button>

                {selectedMatrics.length > 0 && (
                  <button
                    disabled={isBroadcasting}
                    onClick={() => handleTriggerBroadcast('SELECTED')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '9px 16px',
                      borderRadius: '6px',
                      backgroundColor: '#38BDF8',
                      color: '#082F49',
                      border: 'none',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    <Send size={15} /> Send to Selected ({selectedMatrics.length})
                  </button>
                )}

                <button
                  disabled={isBroadcasting}
                  onClick={() => handleTriggerBroadcast('UNSENT_ONLY')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '9px 14px',
                    borderRadius: '6px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #3F3F46',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <RotateCcw size={14} color="#FF9900" /> Resend to Unsent Only
                </button>

                <button
                  disabled={isBroadcasting}
                  onClick={() => handleTriggerBroadcast('UNVOTED_ONLY')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '9px 14px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#EF4444',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Mail size={14} /> Send Urgent Reminder to Unvoted
                </button>
              </div>
            </div>

            {/* Rotation / Dispatch Feedback Alert */}
            {lastBroadcastSummary && (
              <div
                style={{
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  padding: '12px 18px',
                  borderRadius: '10px',
                  color: '#10B981',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle2 size={16} />
                  <span>{lastBroadcastSummary}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setLastBroadcastSummary(null)}
                  style={{ background: 'none', border: 'none', color: '#10B981', cursor: 'pointer', padding: '2px' }}
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Campaign Template Selector Card */}
            <div style={{ backgroundColor: '#171619', padding: '20px 24px', borderRadius: '14px', border: '1px solid #2D2C31' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: 1, minWidth: '280px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#8E8E93', textTransform: 'uppercase' }}>
                      Select Campaign Template
                    </label>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setIsTemplateModalOpen(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          backgroundColor: '#FF9900',
                          border: 'none',
                          color: '#171619',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        <Edit size={12} /> Edit Template Copy
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowTemplatePreview(!showTemplatePreview)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          backgroundColor: '#1E1D21',
                          border: '1px solid #2D2C31',
                          color: '#A1A1AA',
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <Eye size={12} /> {showTemplatePreview ? 'Hide Preview' : 'Show Live Preview'}
                      </button>
                    </div>
                  </div>

                  <select
                    value={broadcastTemplate}
                    onChange={(e) => setBroadcastTemplate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      backgroundColor: '#0E0D0F',
                      border: '1px solid #2D2C31',
                      color: '#FFFFFF',
                      fontSize: '13px',
                    }}
                  >
                    <option value="credentials">1. Voter Credentials Pass (Matric + Auto-Password + Direct Link)</option>
                    <option value="live">2. "Election is Live" Announcement</option>
                    <option value="reminder">3. Urgent Final Reminder (Polls Closing Soon)</option>
                    <option value="custom">4. Custom Announcement Message</option>
                  </select>
                </div>

                <div style={{ fontSize: '12px', color: '#A1A1AA', lineHeight: 1.5, maxWidth: '340px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10B981', fontWeight: 600, marginBottom: '2px' }}>
                    <CheckCircle2 size={14} /> Multi-Account Auto-Rotation Active
                  </div>
                  Emails will dispatch through your configured accounts pool. Each account will automatically rotate when reaching its 500 daily quota.
                </div>
              </div>

              {/* Template Live Preview Drawer/Card */}
              {showTemplatePreview && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '16px',
                    backgroundColor: '#0E0D0F',
                    border: '1px solid #2D2C31',
                    borderRadius: '10px',
                    fontSize: '12px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid #242329', paddingBottom: '6px' }}>
                    <span style={{ color: '#FF9900', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: '11px' }}>
                      Sample Voter Render Preview (Adebayo Johnson — 210001)
                    </span>
                    <span style={{ color: '#8E8E93', fontSize: '11px' }}>
                      Subject: {renderEmailTemplate(emailTemplates[broadcastTemplate] || emailTemplates['credentials'], getSampleVoter(), emailSettings, election.title).subject}
                    </span>
                  </div>
                  <div style={{ color: '#D1D5DB', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontFamily: 'monospace', fontSize: '12px' }}>
                    {renderEmailTemplate(emailTemplates[broadcastTemplate] || emailTemplates['credentials'], getSampleVoter(), emailSettings, election.title).body}
                  </div>
                </div>
              )}

              {broadcastTemplate === 'custom' && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="Custom Email Subject..."
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '6px',
                      backgroundColor: '#0E0D0F',
                      border: '1px solid #2D2C31',
                      color: '#FFFFFF',
                      fontSize: '13px',
                    }}
                  />
                  <textarea
                    rows={3}
                    placeholder="Enter custom announcement body to be transmitted to voters..."
                    value={customBody}
                    onChange={(e) => setCustomBody(e.target.value)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '6px',
                      backgroundColor: '#0E0D0F',
                      border: '1px solid #2D2C31',
                      color: '#FFFFFF',
                      fontSize: '13px',
                    }}
                  />
                </div>
              )}
            </div>

            {/* Filtering & Sorting Controls */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                flexWrap: 'wrap',
                backgroundColor: '#171619',
                padding: '16px 20px',
                borderRadius: '12px',
                border: '1px solid #2D2C31',
              }}
            >
              <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
                <Search size={16} color="#8E8E93" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Search recipients by matric or name..."
                  value={broadcastSearch}
                  onChange={(e) => setBroadcastSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '9px 12px 9px 36px',
                    borderRadius: '6px',
                    backgroundColor: '#0E0D0F',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                />
              </div>

              {/* Status Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Filter size={15} color="#8E8E93" />
                <select
                  value={broadcastFilter}
                  onChange={(e) => setBroadcastFilter(e.target.value as any)}
                  style={{
                    padding: '9px 12px',
                    borderRadius: '6px',
                    backgroundColor: '#0E0D0F',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  <option value="ALL">All Recipients</option>
                  <option value="UNSENT">Never Sent (0 emails)</option>
                  <option value="SENT">Sent At Least Once (≥1)</option>
                  <option value="UNVOTED">Unvoted Students</option>
                  <option value="VOTED">Voted Students</option>
                </select>
              </div>

              {/* Sorting Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ArrowUpDown size={15} color="#8E8E93" />
                <select
                  value={broadcastSort}
                  onChange={(e) => setBroadcastSort(e.target.value as any)}
                  style={{
                    padding: '9px 12px',
                    borderRadius: '6px',
                    backgroundColor: '#0E0D0F',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  <option value="SENT_ASC">Sent Count (Fewest to Most)</option>
                  <option value="SENT_DESC">Sent Count (Most to Fewest)</option>
                  <option value="NAME">Name (A-Z)</option>
                  <option value="MATRIC">Matric Number</option>
                </select>
              </div>
            </div>

            {/* Recipients Table with Tracking & Selection */}
            <div style={{ backgroundColor: '#171619', borderRadius: '12px', border: '1px solid #2D2C31', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2D2C31', backgroundColor: '#1E1D21', color: '#8E8E93', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <th style={{ padding: '14px 18px', width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedMatrics.length > 0 && selectedMatrics.length === broadcastVoters.length}
                          onChange={handleSelectAllBroadcast}
                          style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#FF9900' }}
                        />
                      </th>
                      <th style={{ padding: '14px 18px' }}>Matric No</th>
                      <th style={{ padding: '14px 18px' }}>Recipient Name</th>
                      <th style={{ padding: '14px 18px' }}>Email</th>
                      <th style={{ padding: '14px 18px' }}>Sent Count</th>
                      <th style={{ padding: '14px 18px' }}>Vote Status</th>
                      <th style={{ padding: '14px 18px', textAlign: 'right' }}>Direct Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {broadcastVoters.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '36px', textAlign: 'center', color: '#8E8E93' }}>
                          No recipients match the current filter.
                        </td>
                      </tr>
                    ) : (
                      broadcastVoters.map((voter) => {
                        const isSelected = selectedMatrics.includes(voter.matric);
                        const sentCount = voter.email_sent_count || 0;
                        return (
                          <tr key={voter.matric} style={{ borderBottom: '1px solid #1E1D21', backgroundColor: isSelected ? 'rgba(255, 153, 0, 0.05)' : 'transparent' }}>
                            <td style={{ padding: '12px 18px' }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleSelectVoter(voter.matric)}
                                style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#FF9900' }}
                              />
                            </td>
                            <td style={{ padding: '12px 18px', fontWeight: 700, color: '#FF9900' }}>{voter.matric}</td>
                            <td style={{ padding: '12px 18px', fontWeight: 600 }}>{voter.name}</td>
                            <td style={{ padding: '12px 18px', color: '#A1A1AA', fontSize: '12.5px' }}>{voter.email}</td>
                            <td style={{ padding: '12px 18px' }}>
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  backgroundColor:
                                    sentCount === 0
                                      ? 'rgba(239, 68, 68, 0.15)'
                                      : sentCount === 1
                                      ? 'rgba(56, 189, 248, 0.15)'
                                      : 'rgba(16, 185, 129, 0.15)',
                                  color:
                                    sentCount === 0
                                      ? '#EF4444'
                                      : sentCount === 1
                                      ? '#38BDF8'
                                      : '#10B981',
                                }}
                              >
                                {sentCount} {sentCount === 1 ? 'time sent' : 'times sent'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 18px' }}>
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  backgroundColor: voter.has_voted ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                  color: voter.has_voted ? '#10B981' : '#EF4444',
                                }}
                              >
                                {voter.has_voted ? 'VOTED' : 'NOT VOTED'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                              <button
                                onClick={() => handleIndividualResend(voter.matric)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '5px 10px',
                                  borderRadius: '4px',
                                  backgroundColor: '#1E1D21',
                                  border: '1px solid #3F3F46',
                                  color: '#FF9900',
                                  fontSize: '11.5px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                              >
                                <Send size={12} /> Resend
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* =================================================================== */}
        {/* PAGE 5: DEMOGRAPHIC PARTICIPATION INTELLIGENCE                     */}
        {/* =================================================================== */}
        {currentTab === 'analytics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
              <div>
                <h2 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Demographic Participation Intelligence</h2>
                <p style={{ color: '#8E8E93', fontSize: '13px', marginTop: '4px', margin: 0 }}>
                  Real-time voter distribution and turnout insights segmented across academic levels, halls of residence, gender, and hourly activity.
                </p>
              </div>
              <button
                onClick={openPdfExportModal}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#FF9900',
                  color: '#171619',
                  border: 'none',
                  padding: '10px 18px',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(255, 153, 0, 0.3)',
                  transition: 'all 0.2s ease',
                }}
              >
                <FileSpreadsheet size={16} />
                Export Official PDF Report
              </button>
            </div>

            <DemographicResultsSection
              analytics={analytics}
              loading={loading}
              electionTitle={election.title}
              isAdmin={true}
            />
          </div>
        )}

        {/* =================================================================== */}
        {/* PAGE 6: ELECTION SETTINGS                                           */}
        {/* =================================================================== */}
        {currentTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', maxWidth: '820px' }}>
            <div>
              <h2 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Election Configuration & Customization</h2>
              <p style={{ color: '#8E8E93', fontSize: '13px', marginTop: '4px', margin: 0 }}>
                Customize the election name, organization, public results access, and platform branding. Changes apply platform-wide immediately.
              </p>
            </div>

            <form onSubmit={handleSaveSettings} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ backgroundColor: '#171619', padding: '24px 28px', borderRadius: '14px', border: '1px solid #2D2C31' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '18px', color: '#FF9900' }}>
                  1. Identity & Branding
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                      Election Name / Title
                    </label>
                    <input
                      type="text"
                      value={settingsTitle}
                      onChange={(e) => setSettingsTitle(e.target.value)}
                      required
                      placeholder="e.g. LSS UI Election '25"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '14px',
                      }}
                    />
                    <span style={{ fontSize: '11px', color: '#8E8E93', marginTop: '4px', display: 'block' }}>
                      This title updates the homepage hero, voting ballots, receipt headers, and results banners.
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                        URL Slug Identifier
                      </label>
                      <input
                        type="text"
                        value={settingsSlug}
                        onChange={(e) => setSettingsSlug(e.target.value)}
                        required
                        placeholder="lss-ui-2025"
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          borderRadius: '6px',
                          backgroundColor: '#0E0D0F',
                          border: '1px solid #2D2C31',
                          color: '#FFFFFF',
                          fontSize: '14px',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                        Society / Organization Name
                      </label>
                      <input
                        type="text"
                        value={settingsOrgName}
                        onChange={(e) => setSettingsOrgName(e.target.value)}
                        placeholder="Law Students' Society, University of Ibadan"
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          borderRadius: '6px',
                          backgroundColor: '#0E0D0F',
                          border: '1px solid #2D2C31',
                          color: '#FFFFFF',
                          fontSize: '14px',
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                      Description & Scope
                    </label>
                    <textarea
                      rows={2}
                      value={settingsDesc}
                      onChange={(e) => setSettingsDesc(e.target.value)}
                      placeholder="e.g. Law Students Society Annual Executive Council and LSRC Elections"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '13.5px',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                      Support / Complaint Email
                    </label>
                    <input
                      type="email"
                      value={settingsSupportEmail}
                      onChange={(e) => setSettingsSupportEmail(e.target.value)}
                      placeholder="support@electpoll.com"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '14px',
                      }}
                    />
                  </div>

                  {/* CO-BRANDING LOGOS SECTION */}
                  <div style={{ marginTop: '14px', paddingTop: '16px', borderTop: '1px solid #2D2C31' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 700, color: '#FF9900' }}>
                        Official Co-Branding Logos (Certified PDF Export)
                      </label>
                      <button
                        type="button"
                        onClick={openPdfExportModal}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          backgroundColor: '#FF990018',
                          color: '#FF9900',
                          border: '1px solid #FF990040',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '11.5px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <FileSpreadsheet size={13} />
                        Preview / Export PDF Report
                      </button>
                    </div>
                    <p style={{ color: '#8E8E93', fontSize: '12px', margin: '0 0 14px 0', lineHeight: 1.5 }}>
                      Upload or link up to two official logos (e.g. Faculty, University, or Electoral Commission). They will be rendered in the document header in the exact required layout: <strong>Logo 1 (optional) | Logo 2 (optional) | Electpoll Logo</strong>.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                      {/* Logo 1 Card */}
                      <div style={{ backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', borderRadius: '10px', padding: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#FFFFFF' }}>Logo 1 (Optional - Left)</span>
                          {settingsLogo1 && (
                            <button
                              type="button"
                              onClick={() => setSettingsLogo1('')}
                              style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                          <div
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '8px',
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #374151',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              flexShrink: 0,
                            }}
                          >
                            {settingsLogo1 ? (
                              <img src={settingsLogo1} alt="Logo 1 Preview" style={{ maxWidth: '42px', maxHeight: '42px', objectFit: 'contain' }} />
                            ) : (
                              <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>Empty</span>
                            )}
                          </div>

                          <div style={{ flex: 1 }}>
                            <label
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                backgroundColor: '#1E1D21',
                                border: '1px solid #3B3A3E',
                                color: '#FFFFFF',
                                fontSize: '11.5px',
                                fontWeight: 600,
                                cursor: uploadingLogo1 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <UploadCloud size={13} color="#FF9900" />
                              {uploadingLogo1 ? 'Uploading...' : 'Upload Image (Cloudinary)'}
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                disabled={uploadingLogo1}
                                onChange={handleLogo1Upload}
                              />
                            </label>
                          </div>
                        </div>

                        <input
                          type="text"
                          placeholder="Or paste image URL (https://...)"
                          value={settingsLogo1}
                          onChange={(e) => setSettingsLogo1(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#171619',
                            border: '1px solid #2D2C31',
                            color: '#FFFFFF',
                            fontSize: '12px',
                          }}
                        />
                      </div>

                      {/* Logo 2 Card */}
                      <div style={{ backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', borderRadius: '10px', padding: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#FFFFFF' }}>Logo 2 (Optional - Middle)</span>
                          {settingsLogo2 && (
                            <button
                              type="button"
                              onClick={() => setSettingsLogo2('')}
                              style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                          <div
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '8px',
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #374151',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              flexShrink: 0,
                            }}
                          >
                            {settingsLogo2 ? (
                              <img src={settingsLogo2} alt="Logo 2 Preview" style={{ maxWidth: '42px', maxHeight: '42px', objectFit: 'contain' }} />
                            ) : (
                              <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>Empty</span>
                            )}
                          </div>

                          <div style={{ flex: 1 }}>
                            <label
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                backgroundColor: '#1E1D21',
                                border: '1px solid #3B3A3E',
                                color: '#FFFFFF',
                                fontSize: '11.5px',
                                fontWeight: 600,
                                cursor: uploadingLogo2 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <UploadCloud size={13} color="#FF9900" />
                              {uploadingLogo2 ? 'Uploading...' : 'Upload Image (Cloudinary)'}
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                disabled={uploadingLogo2}
                                onChange={handleLogo2Upload}
                              />
                            </label>
                          </div>
                        </div>

                        <input
                          type="text"
                          placeholder="Or paste image URL (https://...)"
                          value={settingsLogo2}
                          onChange={(e) => setSettingsLogo2(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#171619',
                            border: '1px solid #2D2C31',
                            color: '#FFFFFF',
                            fontSize: '12px',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION: SITE METADATA, BROWSER TAB TITLE & SOCIAL SHARING (SEO) */}
              <div style={{ backgroundColor: '#171619', padding: '24px 28px', borderRadius: '14px', border: '1px solid #2D2C31' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: '#FF99001A', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FF990033' }}>
                      <Globe size={18} color="#FF9900" />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: '#FF9900' }}>
                        2. Site Metadata & Social Sharing (Browser Tab & SEO)
                      </h3>
                    </div>
                  </div>
                  <span style={{ fontSize: '11px', color: '#10B981', backgroundColor: '#10B9811A', border: '1px solid #10B98133', padding: '4px 10px', borderRadius: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10B981' }}></span>
                    Live Browser & Social Crawler Sync
                  </span>
                </div>
                <p style={{ color: '#8E8E93', fontSize: '12px', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                  Customize how this election is branded across browser window tabs, search engine indices, and social media rich link cards (WhatsApp, Twitter/X, Telegram, Slack, Facebook).
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  {/* Meta Title Field */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '4px' }}>
                      <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA' }}>
                        Custom Browser Tab / Meta Title
                      </label>
                      <span style={{ fontSize: '11px', color: '#71717A' }}>
                        Leave blank to default to: <em>{settingsTitle ? `${settingsTitle} - Electpoll` : 'Electpoll'}</em>
                      </span>
                    </div>
                    <input
                      type="text"
                      value={settingsMetaTitle}
                      onChange={(e) => setSettingsMetaTitle(e.target.value)}
                      placeholder={settingsTitle ? `${settingsTitle} - Electpoll` : "e.g. Annual General Elections 2026 - Electpoll"}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '14px',
                      }}
                    />
                    <span style={{ fontSize: '11px', color: '#8E8E93', marginTop: '4px', display: 'block' }}>
                      This explicitly overrides the browser tab &lt;title&gt; and social link headings, preventing outdated titles like "LSS Election '25" on new elections.
                    </span>
                  </div>

                  {/* Meta Description Field */}
                  <div>
                    <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                      Meta Description (Search Snippet & Social Message)
                    </label>
                    <textarea
                      rows={2}
                      value={settingsMetaDesc}
                      onChange={(e) => setSettingsMetaDesc(e.target.value)}
                      placeholder={settingsTitle ? `Official digital voting portal for ${settingsTitle}. Transparent, secure, and tamper-proof e-voting powered by Electpoll.` : "Official voting portal powered by Electpoll."}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '13.5px',
                        lineHeight: 1.4,
                      }}
                    />
                    <span style={{ fontSize: '11px', color: '#8E8E93', marginTop: '4px', display: 'block' }}>
                      Summarizes the election when voters paste your link into WhatsApp groups, Twitter, or search results.
                    </span>
                  </div>

                  {/* Social Share Preview Image */}
                  <div>
                    <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                      Social Share Preview Card Image (OpenGraph / Twitter Thumbnail)
                    </label>
                    <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <div
                        style={{
                          width: '96px',
                          height: '54px',
                          borderRadius: '8px',
                          backgroundColor: '#0E0D0F',
                          border: '1px solid #2D2C31',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          flexShrink: 0,
                        }}
                      >
                        {settingsOgImage || settingsLogo1 ? (
                          <img
                            src={settingsOgImage || settingsLogo1}
                            alt="Social Share Thumbnail"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <img
                            src="/assets/images/favicon.png"
                            alt="Electpoll Fallback"
                            style={{ maxWidth: '28px', maxHeight: '28px', objectFit: 'contain' }}
                          />
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '220px' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3B3A3E',
                              color: '#FFFFFF',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: uploadingOgImage ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <UploadCloud size={13} color="#FF9900" />
                            {uploadingOgImage ? 'Uploading...' : 'Upload Image (Cloudinary)'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={uploadingOgImage}
                              onChange={handleOgImageUpload}
                            />
                          </label>

                          {settingsOgImage && (
                            <button
                              type="button"
                              onClick={() => setSettingsOgImage('')}
                              style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                            >
                              Reset to Default
                            </button>
                          )}
                        </div>

                        <input
                          type="text"
                          placeholder="Or paste direct image URL (https://...)"
                          value={settingsOgImage}
                          onChange={(e) => setSettingsOgImage(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#0E0D0F',
                            border: '1px solid #2D2C31',
                            color: '#FFFFFF',
                            fontSize: '12px',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Meta Keywords */}
                  <div>
                    <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                      Meta Keywords (Search Tags)
                    </label>
                    <input
                      type="text"
                      value={settingsMetaKeywords}
                      onChange={(e) => setSettingsMetaKeywords(e.target.value)}
                      placeholder="e.g. election, online voting, electpoll, student council, democracy"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '14px',
                      }}
                    />
                  </div>

                  {/* LIVE PREVIEWS */}
                  <div style={{ marginTop: '6px', paddingTop: '16px', borderTop: '1px dashed #2D2C31' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Share2 size={14} color="#FF9900" />
                      <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#E4E4E7' }}>
                        Live Social & Browser Previews
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                      {/* Browser Tab Preview */}
                      <div style={{ backgroundColor: '#0A0A0C', border: '1px solid #27272A', borderRadius: '8px', padding: '12px' }}>
                        <span style={{ fontSize: '11px', color: '#71717A', display: 'block', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Browser Tab Preview
                        </span>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#18181B', border: '1px solid #3F3F46', padding: '6px 12px', borderRadius: '8px 8px 0 0', maxWidth: '100%' }}>
                          <img src="/assets/images/favicon.png" alt="favicon" style={{ width: '14px', height: '14px', flexShrink: 0 }} />
                          <span style={{ fontSize: '12px', color: '#FFFFFF', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                            {settingsMetaTitle || (settingsTitle ? `${settingsTitle} - Electpoll` : 'Electpoll - Secure Online Voting')}
                          </span>
                          <span style={{ fontSize: '12px', color: '#71717A', marginLeft: '4px', cursor: 'default' }}>×</span>
                        </div>
                        <div style={{ height: '3px', backgroundColor: '#FF9900', borderRadius: '0 0 2px 2px' }}></div>
                      </div>

                      {/* WhatsApp / Social Card Preview */}
                      <div style={{ backgroundColor: '#0A0A0C', border: '1px solid #27272A', borderRadius: '8px', padding: '12px' }}>
                        <span style={{ fontSize: '11px', color: '#71717A', display: 'block', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          WhatsApp / Twitter Share Card Preview
                        </span>
                        <div style={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ height: '90px', backgroundColor: '#27272A', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            <img
                              src={settingsOgImage || settingsLogo1 || '/assets/images/favicon.png'}
                              alt="Share preview"
                              style={{ width: '100%', height: '100%', objectFit: (settingsOgImage || settingsLogo1) ? 'cover' : 'contain', padding: (settingsOgImage || settingsLogo1) ? 0 : '16px' }}
                            />
                          </div>
                          <div style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: '10px', color: '#A1A1AA', textTransform: 'uppercase', letterSpacing: '0.5px' }}>electpoll.com</span>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF', marginTop: '2px', lineHeight: 1.3 }}>
                              {settingsMetaTitle || (settingsTitle ? `${settingsTitle} - Electpoll` : 'Electpoll - Secure Online Voting')}
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#9CA3AF', marginTop: '4px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {settingsMetaDesc || (settingsDesc ? settingsDesc : `Official digital voting portal for ${settingsTitle || 'the election'}. Secure, transparent, and tamper-proof.`)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION: OFFICIAL CERTIFIED SIGNATORIES & PDF REPORT SIGN-OFF */}
              <div style={{ backgroundColor: '#171619', padding: '24px 28px', borderRadius: '14px', border: '1px solid #2D2C31' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ShieldCheck size={18} color="#FF9900" />
                    <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: '#FF9900' }}>
                      3. Official Certified Signatories (PDF Report Sign-Off)
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={openPdfExportModal}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      borderRadius: '8px',
                      backgroundColor: '#1E1D21',
                      border: '1px solid #3B3A3E',
                      color: '#E4E4E7',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <Printer size={13} color="#FF9900" />
                    Preview PDF Certificate
                  </button>
                </div>
                <p style={{ color: '#8E8E93', fontSize: '13px', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                  Configure the names, official titles, and signature images of the returning officer and observer. These appear directly over the certification lines in the official PDF report export.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                  {/* Signatory 1: Commissioner */}
                  <div style={{ backgroundColor: '#0E0D0F', padding: '18px', borderRadius: '10px', border: '1px solid #262529' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase' }}>
                        Chief Electoral Commissioner / Returning Officer
                      </span>
                      {settingsCommSig && (
                        <button
                          type="button"
                          onClick={() => setSettingsCommSig('')}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '4px' }}>
                        Signatory Full Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Olapade Bukola"
                        value={settingsCommName}
                        onChange={(e) => setSettingsCommName(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '4px' }}>
                        Official Role Title
                      </label>
                      <input
                        type="text"
                        placeholder="Chief Electoral Commissioner / Returning Officer"
                        value={settingsCommTitle}
                        onChange={(e) => setSettingsCommTitle(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                        Signature Image (Cloudinary or Direct URL)
                      </label>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                        <div
                          style={{
                            width: '100px',
                            height: '52px',
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #374151',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          {settingsCommSig ? (
                            <img src={settingsCommSig} alt="Commissioner Signature" style={{ maxHeight: '46px', maxWidth: '92px', objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>No Signature</span>
                          )}
                        </div>

                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3B3A3E',
                              color: '#FFFFFF',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: uploadingCommSig ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <UploadCloud size={13} color="#FF9900" />
                            {uploadingCommSig ? 'Uploading...' : 'Upload Signature'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={uploadingCommSig}
                              onChange={handleCommSigUpload}
                            />
                          </label>
                        </div>
                      </div>

                      <input
                        type="text"
                        placeholder="Or enter direct signature URL..."
                        value={settingsCommSig}
                        onChange={(e) => setSettingsCommSig(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Signatory 2: Observer */}
                  <div style={{ backgroundColor: '#0E0D0F', padding: '18px', borderRadius: '10px', border: '1px solid #262529' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase' }}>
                        Faculty / Staff Electoral Observer
                      </span>
                      {settingsObsSig && (
                        <button
                          type="button"
                          onClick={() => setSettingsObsSig('')}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '4px' }}>
                        Signatory Full Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Dr. J. O. Ogundele"
                        value={settingsObsName}
                        onChange={(e) => setSettingsObsName(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '4px' }}>
                        Official Role Title
                      </label>
                      <input
                        type="text"
                        placeholder="Faculty / Staff Electoral Observer"
                        value={settingsObsTitle}
                        onChange={(e) => setSettingsObsTitle(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                        Signature Image (Cloudinary or Direct URL)
                      </label>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                        <div
                          style={{
                            width: '100px',
                            height: '52px',
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #374151',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          {settingsObsSig ? (
                            <img src={settingsObsSig} alt="Observer Signature" style={{ maxHeight: '46px', maxWidth: '92px', objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>No Signature</span>
                          )}
                        </div>

                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3B3A3E',
                              color: '#FFFFFF',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: uploadingObsSig ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <UploadCloud size={13} color="#FF9900" />
                            {uploadingObsSig ? 'Uploading...' : 'Upload Signature'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={uploadingObsSig}
                              onChange={handleObsSigUpload}
                            />
                          </label>
                        </div>
                      </div>

                      <input
                        type="text"
                        placeholder="Or enter direct signature URL..."
                        value={settingsObsSig}
                        onChange={(e) => setSettingsObsSig(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Signatory 3: Electpoll Platform Representative & Systems Auditor */}
                  <div style={{ backgroundColor: '#0E0D0F', padding: '18px', borderRadius: '10px', border: '1px solid #262529' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase' }}>
                        Electpoll Representative & Auditor
                      </span>
                      {settingsElectpollRepSig && (
                        <button
                          type="button"
                          onClick={() => setSettingsElectpollRepSig('')}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '4px' }}>
                        Representative Full Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Bukola Olapade"
                        value={settingsElectpollRepName}
                        onChange={(e) => setSettingsElectpollRepName(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '4px' }}>
                        Official Role Title
                      </label>
                      <input
                        type="text"
                        placeholder="Electpoll Platform Representative & Systems Auditor"
                        value={settingsElectpollRepTitle}
                        onChange={(e) => setSettingsElectpollRepTitle(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                        Signature Image (Cloudinary or Direct URL)
                      </label>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                        <div
                          style={{
                            width: '100px',
                            height: '52px',
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #374151',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          {settingsElectpollRepSig ? (
                            <img src={settingsElectpollRepSig} alt="Electpoll Representative Signature" style={{ maxHeight: '46px', maxWidth: '92px', objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>No Signature</span>
                          )}
                        </div>

                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3B3A3E',
                              color: '#FFFFFF',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: uploadingElectpollRepSig ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <UploadCloud size={13} color="#FF9900" />
                            {uploadingElectpollRepSig ? 'Uploading...' : 'Upload Signature'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={uploadingElectpollRepSig}
                              onChange={handleElectpollRepSigUpload}
                            />
                          </label>
                        </div>
                      </div>

                      <input
                        type="text"
                        placeholder="Or enter direct signature URL..."
                        value={settingsElectpollRepSig}
                        onChange={(e) => setSettingsElectpollRepSig(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', backgroundColor: '#171619', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 4: VOTING SCHEDULE & TIME WINDOWS */}
              <div style={{ backgroundColor: '#171619', padding: '24px 28px', borderRadius: '14px', border: '1px solid #2D2C31' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <Clock size={18} color="#FF9900" />
                  <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: '#FF9900' }}>
                    4. Voting Schedule & Time Windows
                  </h3>
                </div>
                <p style={{ color: '#8E8E93', fontSize: '13px', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                  Set the official voting window. Voters cannot log in or cast ballots before the Start Time. When the deadline passes, voting automatically closes.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                      Voting Start Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={settingsStartTime}
                      onChange={(e) => setSettingsStartTime(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        colorScheme: 'dark',
                      }}
                    />
                    <div style={{ fontSize: '11.5px', color: '#8E8E93', marginTop: '6px' }}>
                      Voter login portal unlocks automatically when this time arrives.
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                      Voting End Date & Time (Deadline)
                    </label>
                    <input
                      type="datetime-local"
                      value={settingsEndTime}
                      onChange={(e) => setSettingsEndTime(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        colorScheme: 'dark',
                      }}
                    />
                    <div style={{ fontSize: '11.5px', color: '#8E8E93', marginTop: '6px' }}>
                      Polls automatically close and further voting is blocked.
                    </div>
                  </div>
                </div>

                {/* Status indicator badge */}
                <div style={{ marginTop: '18px', padding: '12px 16px', borderRadius: '8px', backgroundColor: '#131215', border: '1px solid #2D2C31', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px' }}>
                    <span style={{ color: '#8E8E93' }}>Schedule Status:</span>
                    {(() => {
                      const now = new Date();
                      const start = settingsStartTime ? new Date(settingsStartTime) : null;
                      const end = settingsEndTime ? new Date(settingsEndTime) : null;

                      if (start && now < start) {
                        return (
                          <span style={{ padding: '3px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', backgroundColor: 'rgba(255, 153, 0, 0.15)', color: '#FF9900', border: '1px solid rgba(255, 153, 0, 0.3)' }}>
                            Upcoming (Locked until {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
                          </span>
                        );
                      }
                      if (end && now > end) {
                        return (
                          <span style={{ padding: '3px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                            Closed (Deadline passed)
                          </span>
                        );
                      }
                      return (
                        <span style={{ padding: '3px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10B981', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                          Live / Open for Voting
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#8E8E93' }}>
                    Leave blank to allow voting whenever election status is active.
                  </div>
                </div>
              </div>

              {/* Public Live Results Security Settings */}
              <div style={{ backgroundColor: '#171619', padding: '24px 28px', borderRadius: '14px', border: '1px solid #2D2C31' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '18px', color: '#38BDF8' }}>
                  5. Results Visibility & Security
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settingsAllowLive}
                      onChange={(e) => setSettingsAllowLive(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#FF9900' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>Enable Live Results Portal</div>
                      <div style={{ fontSize: '12px', color: '#8E8E93' }}>
                        Allows users to visit the `/results` page to track election vote tallies in real-time.
                      </div>
                    </div>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settingsProtectResults}
                      onChange={(e) => setSettingsProtectResults(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#FF9900' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>Require Access Password to View Results</div>
                      <div style={{ fontSize: '12px', color: '#8E8E93' }}>
                        Gate the results page behind a secret security code known only to election officials or observers.
                      </div>
                    </div>
                  </label>

                  {settingsProtectResults && (
                    <div style={{ paddingLeft: '30px' }}>
                      <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                        Results Secret Access Password
                      </label>
                      <input
                        type="text"
                        value={settingsResultsPassword}
                        onChange={(e) => setSettingsResultsPassword(e.target.value)}
                        placeholder="Enter results security password..."
                        style={{
                          width: '100%',
                          maxWidth: '360px',
                          padding: '10px 14px',
                          borderRadius: '6px',
                          backgroundColor: '#0E0D0F',
                          border: '1px solid #2D2C31',
                          color: '#FFFFFF',
                          fontSize: '14px',
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Demographic Participation Tracking Settings */}
              <div style={{ backgroundColor: '#171619', padding: '24px 28px', borderRadius: '14px', border: '1px solid #2D2C31' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px', color: '#10B981' }}>
                  6. Demographic Participation Tracking & Visibility
                </h3>
                <p style={{ color: '#8E8E93', fontSize: '13px', lineHeight: 1.5, marginBottom: '20px' }}>
                  Choose which student demographic cohorts to collect, analyze, and display on the public results page and official PDF report. If your election only collects specific data (such as Matric and Level only), simply uncheck the unused dimensions to hide them everywhere.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                  {/* Academic Level */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      backgroundColor: '#0E0D0F',
                      border: settingsShowDemographicsLevel ? '1.5px solid #FF9900' : '1px solid #2D2C31',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={settingsShowDemographicsLevel}
                      onChange={(e) => setSettingsShowDemographicsLevel(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#FF9900', marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#FFFFFF', marginBottom: '3px' }}>
                        Academic Level Cohorts
                      </div>
                      <div style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.4 }}>
                        Track voter turnout, graphs, and candidate distributions broken down across academic classes.
                      </div>
                    </div>
                  </label>

                  {/* Gender Breakdown */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      backgroundColor: '#0E0D0F',
                      border: settingsShowDemographicsGender ? '1.5px solid #FF9900' : '1px solid #2D2C31',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={settingsShowDemographicsGender}
                      onChange={(e) => setSettingsShowDemographicsGender(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#FF9900', marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#FFFFFF', marginBottom: '3px' }}>
                        Gender Distribution
                      </div>
                      <div style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.4 }}>
                        Track female vs. male voter turnout. Uncheck if voter roster does not collect gender information.
                      </div>
                    </div>
                  </label>

                  {/* Hall of Residence */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      backgroundColor: '#0E0D0F',
                      border: settingsShowDemographicsHall ? '1.5px solid #FF9900' : '1px solid #2D2C31',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={settingsShowDemographicsHall}
                      onChange={(e) => setSettingsShowDemographicsHall(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#FF9900', marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#FFFFFF', marginBottom: '3px' }}>
                        Hall of Residence / Hostel
                      </div>
                      <div style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.4 }}>
                        Track residential rankings and hostel voting. Uncheck for faculty or non-residential elections.
                      </div>
                    </div>
                  </label>

                  {/* Hourly Turnout Timeline */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      backgroundColor: '#0E0D0F',
                      border: settingsShowDemographicsHourly ? '1.5px solid #FF9900' : '1px solid #2D2C31',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={settingsShowDemographicsHourly}
                      onChange={(e) => setSettingsShowDemographicsHourly(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#FF9900', marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#FFFFFF', marginBottom: '3px' }}>
                        Hourly Voting Velocity ⏱
                      </div>
                      <div style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.4 }}>
                        Track votes cast per hour, peak voting hour, and voter velocity timeline across the election.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Submit Save Button */}
              <button
                type="submit"
                disabled={savingSettings}
                style={{
                  padding: '13px 28px',
                  borderRadius: '8px',
                  backgroundColor: '#FF9900',
                  color: '#171619',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: 'fit-content',
                }}
              >
                <CheckCircle2 size={18} />
                <span>{savingSettings ? 'Saving Settings...' : 'Save Election Settings'}</span>
              </button>
            </form>

            {/* Danger Zone Card */}
            <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)', padding: '24px 28px', borderRadius: '14px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#EF4444', marginBottom: '8px' }}>
                <AlertTriangle size={18} />
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Danger Zone: Reset Election Data</h3>
              </div>
              <p style={{ color: '#8E8E93', fontSize: '13px', lineHeight: 1.5, marginBottom: '16px' }}>
                Purge test votes and reset all accredited student records back to unvoted state. This is irreversible.
              </p>
              <button
                onClick={handlePurgeVotes}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid #EF4444',
                  color: '#EF4444',
                  fontSize: '12.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Purge Ballots & Reset Votes
              </button>
            </div>
          </div>
        )}
      </main>

      {/* =================================================================== */}
      {/* MODAL: ADD / EDIT ROLE WITH "WHO CAN VOTE" ELIGIBILITY              */}
      {/* =================================================================== */}
      {roleModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div style={{ backgroundColor: '#171619', border: '1px solid #2D2C31', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '520px', position: 'relative' }}>
            <button
              onClick={() => setRoleModalOpen(false)}
              style={{ position: 'absolute', right: '20px', top: '20px', background: 'transparent', border: 'none', color: '#8E8E93', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 18px 0' }}>
              {editingRoleKey ? 'Edit Role & Eligibility' : 'Create New Office / Role'}
            </h3>

            <form onSubmit={handleSaveRole} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Role Title
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 100L Constituency Representative"
                  value={roleNameInput}
                  onChange={(e) => setRoleNameInput(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              {!editingRoleKey && (
                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                    Role Identifier Key (Slug)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Rep100"
                    value={roleKeyInput}
                    onChange={(e) => setRoleKeyInput(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                  />
                </div>
              )}

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Maximum Choices Allowed per Voter
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={roleMaxInput}
                  onChange={(e) => setRoleMaxInput(Number(e.target.value))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              {/* Who Can Vote Selection */}
              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 700, color: '#FF9900', display: 'block', marginBottom: '8px' }}>
                  Who Can Vote for This Role? (Voter Eligibility)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px' }}>
                  {['100', '200', '300', '400', '500'].map((lvl) => {
                    const isChecked = roleLevelsInput.includes(lvl);
                    return (
                      <label
                        key={lvl}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          backgroundColor: isChecked ? 'rgba(255, 153, 0, 0.15)' : '#0E0D0F',
                          border: isChecked ? '1px solid #FF9900' : '1px solid #2D2C31',
                          cursor: 'pointer',
                          fontSize: '12.5px',
                          fontWeight: 600,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRoleLevelsInput((prev) => [...prev, lvl]);
                            } else {
                              setRoleLevelsInput((prev) => prev.filter((l) => l !== lvl));
                            }
                          }}
                          style={{ width: '14px', height: '14px', accentColor: '#FF9900' }}
                        />
                        {lvl}L
                      </label>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setRoleLevelsInput(['100', '200', '300', '400', '500'])}
                    style={{ background: 'transparent', border: 'none', color: '#38BDF8', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                  >
                    Select All Levels
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoleLevelsInput([])}
                    style={{ background: 'transparent', border: 'none', color: '#8E8E93', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <button
                type="submit"
                style={{
                  marginTop: '10px',
                  padding: '11px',
                  borderRadius: '6px',
                  backgroundColor: '#FF9900',
                  color: '#171619',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Save Role & Rules
              </button>
            </form>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL: ADD / EDIT CANDIDATE                                         */}
      {/* =================================================================== */}
      {candidateModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div style={{ backgroundColor: '#171619', border: '1px solid #2D2C31', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '520px', position: 'relative' }}>
            <button
              onClick={() => setCandidateModalOpen(false)}
              style={{ position: 'absolute', right: '20px', top: '20px', background: 'transparent', border: 'none', color: '#8E8E93', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 18px 0' }}>
              {editingCandidateId ? 'Edit Candidate Profile' : 'Add Candidate to ' + rolesData[targetRoleKey]?.name}
            </h3>

            <form onSubmit={handleSaveCandidate} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Candidate Full Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Ajakaye Adebodun Ademola"
                  value={candidateNameInput}
                  onChange={(e) => setCandidateNameInput(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Campaign Nickname / Moniker (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Triple A"
                  value={candidateNicknameInput}
                  onChange={(e) => setCandidateNicknameInput(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              {/* Photo Upload / URL */}
              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Candidate Portrait
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <img
                    src={candidateImageInput || '/assets/images/userdefault.png'}
                    alt="Preview"
                    style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #FF9900' }}
                  />
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageFilePick}
                      disabled={uploadingImage}
                      style={{ fontSize: '12px', color: '#A1A1AA' }}
                    />
                    <div style={{ fontSize: '11px', color: '#8E8E93', marginTop: '4px' }}>
                      {uploadingImage ? 'Uploading image...' : 'Direct upload to Cloudinary or specify image URL below'}
                    </div>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Or enter direct image URL (/assets/images/11.jpg)..."
                  value={candidateImageInput}
                  onChange={(e) => setCandidateImageInput(e.target.value)}
                  style={{ width: '100%', marginTop: '8px', padding: '8px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12.5px' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Bio / Manifesto Snippet
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Dedicated to progressive representation and academic excellence..."
                  value={candidateBioInput}
                  onChange={(e) => setCandidateBioInput(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12.5px' }}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={candidateAbstainInput}
                  onChange={(e) => setCandidateAbstainInput(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#FF9900' }}
                />
                <span>Set as Abstain / Neutral Option ("No" or "Indecisive")</span>
              </label>

              {/* Mid-Election Candidate Disqualification Section */}
              <div
                style={{
                  padding: '14px',
                  borderRadius: '10px',
                  backgroundColor: candidateDisqualifiedInput ? '#3B1215' : '#1A191D',
                  border: candidateDisqualifiedInput ? '1.5px solid #EF4444' : '1px solid #2D2C31',
                  transition: 'all 0.2s ease',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13px', cursor: 'pointer', fontWeight: 700, color: candidateDisqualifiedInput ? '#F87171' : '#E5E7EB' }}>
                  <input
                    type="checkbox"
                    checked={candidateDisqualifiedInput}
                    onChange={(e) => setCandidateDisqualifiedInput(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#EF4444' }}
                  />
                  <span>Disqualify Candidate (Mid-Election)</span>
                </label>
                <p style={{ fontSize: '11.5px', color: '#9CA3AF', margin: '4px 0 0 25px', lineHeight: 1.35 }}>
                  The candidate remains visible on the voting ballot for electoral transparency, but will be locked (unclickable) and clearly tagged as Disqualified.
                </p>

                {candidateDisqualifiedInput && (
                  <div style={{ marginTop: '12px', marginLeft: '25px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#FCA5A5', display: 'block', marginBottom: '4px' }}>
                      Reason for Disqualification (Displayed in fine print on voting ballot):
                    </label>
                    <textarea
                      rows={2}
                      required={candidateDisqualifiedInput}
                      placeholder="e.g. Disqualified by Electoral Commission for electoral code violation (Section 4b)..."
                      value={candidateDisqualifyReasonInput}
                      onChange={(e) => setCandidateDisqualifyReasonInput(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #7F1D1D',
                        color: '#FFFFFF',
                        fontSize: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                      This explanation will be shown to voters directly below the candidate's name on the voting screen.
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                style={{
                  marginTop: '10px',
                  padding: '11px',
                  borderRadius: '6px',
                  backgroundColor: '#FF9900',
                  color: '#171619',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Save Candidate
              </button>
            </form>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL: QUICK DISQUALIFY CANDIDATE (MID-ELECTION)                    */}
      {/* =================================================================== */}
      {quickDisqualifyModalOpen && quickDisqualifyCandidate && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 120, padding: '20px' }}>
          <div style={{ backgroundColor: '#171619', border: '1.5px solid #EF4444', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '480px', position: 'relative', boxShadow: '0 0 30px rgba(239, 68, 68, 0.25)' }}>
            <button
              onClick={() => {
                setQuickDisqualifyModalOpen(false);
                setQuickDisqualifyCandidate(null);
              }}
              style={{ position: 'absolute', right: '20px', top: '20px', background: 'transparent', border: 'none', color: '#8E8E93', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', backgroundColor: '#7F1D1D44', border: '1px solid #EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Ban size={20} color="#EF4444" />
              </div>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: '#FFFFFF' }}>
                  Disqualify Candidate Mid-Election
                </h3>
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
                  Candidate: <strong style={{ color: '#F87171' }}>{quickDisqualifyCandidate.cand.name}</strong>
                </div>
              </div>
            </div>

            <div style={{ padding: '12px', backgroundColor: '#381316', border: '1px solid #7F1D1D', borderRadius: '8px', fontSize: '12px', color: '#FCA5A5', lineHeight: 1.4, marginBottom: '16px' }}>
              <strong>Notice:</strong> This candidate will remain visible on the voting page for transparency, but will be locked (unclickable) and cannot receive any more votes. Existing selections by voters will be blocked.
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                executeDisqualifyUpdate(
                  quickDisqualifyCandidate.roleKey,
                  quickDisqualifyCandidate.cand.id,
                  true,
                  quickDisqualifyReason.trim()
                );
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
            >
              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 700, color: '#D1D5DB', display: 'block', marginBottom: '6px' }}>
                  Disqualification Reason <span style={{ color: '#EF4444' }}>*</span> (Will show in fine print to voters):
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="e.g. Disqualified by the Electoral Committee for electoral code violation (Section 4b)..."
                  value={quickDisqualifyReason}
                  onChange={(e) => setQuickDisqualifyReason(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#0E0D0F',
                    border: '1px solid #7F1D1D',
                    color: '#FFFFFF',
                    fontSize: '12.5px',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setQuickDisqualifyModalOpen(false);
                    setQuickDisqualifyCandidate(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: '8px',
                    backgroundColor: '#2D2C31',
                    color: '#FFFFFF',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingDisqualification || !quickDisqualifyReason.trim()}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: '8px',
                    backgroundColor: '#DC2626',
                    color: '#FFFFFF',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: savingDisqualification || !quickDisqualifyReason.trim() ? 'not-allowed' : 'pointer',
                    opacity: savingDisqualification || !quickDisqualifyReason.trim() ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <Ban size={14} />
                  <span>{savingDisqualification ? 'Updating...' : 'Confirm Disqualification'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL: IMPORT VOTERS CSV                                            */}
      {/* =================================================================== */}
      <CsvImportModal
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        electionId={election.id}
        electionTitle={election.title}
        onImportSuccess={async (result: any, importedVoters?: any[]) => {
          const count = typeof result === 'object' ? result.count : result;
          const newAdded = typeof result === 'object' && typeof result.newAdded === 'number' ? result.newAdded : count;
          const updated = typeof result === 'object' && typeof result.updated === 'number' ? result.updated : 0;
          const total = typeof result === 'object' ? result.totalAccredited : undefined;

          if (importedVoters && importedVoters.length > 0) {
            setRecentBatchVoters(importedVoters);
            try {
              localStorage.setItem('electpoll_recent_batch_voters', JSON.stringify(importedVoters));
            } catch {}
          }

          if (newAdded > 0) {
            showToast(`Accredited ${newAdded} new voter${newAdded === 1 ? '' : 's'} into ${election.title}!${updated > 0 ? ` (${updated} updated)` : ''}${total ? ` Total: ${total}` : ''}`);
          } else if (updated > 0) {
            showToast(`All ${updated} voter records already existed in ${election.title} (records refreshed). Total: ${total || voters.length}`);
          } else {
            showToast(`Processed ${count} voters into ${election.title}!`);
          }

          // Immediately re-fetch voters & election summary
          const updatedVoters = await api.getVoters(election.id);
          setVoters(updatedVoters);
          const electionsList = await api.getAdminElections();
          if (electionsList.length > 0) {
            setElection(electionsList[0]);
          }
        }}
      />

      {/* =================================================================== */}
      {/* MODAL: EXPORT ROSTER / CREDENTIALS OPTIONS                          */}
      {/* =================================================================== */}
      {exportModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div style={{ backgroundColor: '#171619', border: '1px solid #2D2C31', borderRadius: '14px', padding: '24px', width: '100%', maxWidth: '480px', position: 'relative' }}>
            <button
              onClick={() => setExportModalOpen(false)}
              style={{ position: 'absolute', right: '18px', top: '18px', background: 'transparent', border: 'none', color: '#8E8E93', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 6px 0', color: '#FFFFFF' }}>
              Export Voter Credentials (CSV)
            </h3>
            <p style={{ fontSize: '13px', color: '#8E8E93', margin: '0 0 18px 0', lineHeight: 1.4 }}>
              Choose which voter records and credentials to export from {election.title}:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Option 1: Most Recent Batch (if available) */}
              {recentBatchVoters && recentBatchVoters.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    handleExportRecentBatchCsv();
                    setExportModalOpen(false);
                  }}
                  style={{
                    padding: '14px 16px',
                    borderRadius: '8px',
                    border: '1.5px solid #FF9900',
                    backgroundColor: 'rgba(255, 153, 0, 0.08)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13.5px', fontWeight: 800, color: '#FF9900' }}>
                      ⚡ Most Recent Batch ({recentBatchVoters.length} Voters)
                    </span>
                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', backgroundColor: '#FF9900', color: '#171619', fontWeight: 800 }}>
                      RECOMMENDED
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#A1A1AA', lineHeight: 1.4 }}>
                    Download just the {recentBatchVoters.length} newly added or updated students from your latest CSV upload, with their generated access passwords.
                  </div>
                </button>
              )}

              {/* Option 2: Latest Added Voters (Top N) */}
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: '1px solid #2D2C31',
                  backgroundColor: '#0E0D0F',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#E4E4E7', marginBottom: '4px' }}>
                  🕒 Latest Added / Enrolled Students
                </div>
                <div style={{ fontSize: '12px', color: '#8E8E93', marginBottom: '10px' }}>
                  Export the most recently registered students directly from the live database:
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {[10, 15, 20, 50].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => {
                        handleExportLatestAddedCsv(num);
                        setExportModalOpen(false);
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#1E1D21',
                        border: '1px solid #3F3F46',
                        color: '#F4F4F5',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Top {num}
                    </button>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}>
                    <input
                      type="number"
                      min={1}
                      max={voters.length}
                      value={exportCustomCount}
                      onChange={(e) => setExportCustomCount(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{
                        width: '56px',
                        padding: '5px 8px',
                        borderRadius: '5px',
                        backgroundColor: '#171619',
                        border: '1px solid #3F3F46',
                        color: '#FFFFFF',
                        fontSize: '12px',
                        textAlign: 'center',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        handleExportLatestAddedCsv(exportCustomCount);
                        setExportModalOpen(false);
                      }}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '5px',
                        backgroundColor: '#FF9900',
                        color: '#171619',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Export
                    </button>
                  </div>
                </div>
              </div>

              {/* Option 3: Full Roster */}
              <button
                type="button"
                onClick={() => {
                  handleExportFullRosterCsv();
                  setExportModalOpen(false);
                }}
                style={{
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: '1px solid #2D2C31',
                  backgroundColor: '#0E0D0F',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#E4E4E7', marginBottom: '4px' }}>
                  📋 Full Accreditation Roster ({filteredVoters.length} Voters)
                </div>
                <div style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.4 }}>
                  Export all accredited voter records matching your current table filters ({statusFilter}, {levelFilter}).
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL: EMAIL TEMPLATES EDITOR                                       */}
      {/* =================================================================== */}
      <EmailTemplateModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        templates={emailTemplates}
        settings={emailSettings}
        electionTitle={election.title}
        onSaveTemplates={handleSaveEmailTemplates}
        onSendTestEmail={(rendered) => {
          showToast(`Test preview sent to ${rendered.to}`);
        }}
      />

      {/* =================================================================== */}
      {/* MODAL: EMAIL CONFIG & GMAIL ACCOUNT POOL ROTATION                   */}
      {/* =================================================================== */}
      <EmailConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        settings={emailSettings}
        electionTitle={election.title}
        onSaveSettings={handleSaveEmailSettings}
        onTestAccount={handleTestSenderAccount}
      />

      {/* =================================================================== */}
      {/* MODAL: ADD SINGLE VOTER                                             */}
      {/* =================================================================== */}
      {addVoterModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div style={{ backgroundColor: '#171619', border: '1px solid #2D2C31', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '480px', position: 'relative' }}>
            <button
              onClick={() => setAddVoterModalOpen(false)}
              style={{ position: 'absolute', right: '20px', top: '20px', background: 'transparent', border: 'none', color: '#8E8E93', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 18px 0' }}>
              Enroll Accredited Voter
            </h3>

            <form onSubmit={handleAddSingleVoter} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Matric Number *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 210045"
                  value={newVoterMatric}
                  onChange={(e) => setNewVoterMatric(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Student Full Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Damilola Alabi"
                  value={newVoterName}
                  onChange={(e) => setNewVoterName(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="damilola@student.ui.edu.ng"
                  value={newVoterEmail}
                  onChange={(e) => setNewVoterEmail(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA' }}>
                    Voting Password / Access Code
                  </label>
                  <button
                    type="button"
                    onClick={() => setNewVoterPassword(generateVoterPassword())}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#FF9900',
                      fontSize: '11.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <RotateCcw size={12} /> Regenerate Code
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="7-digit code (e.g. 7M3H2WT)"
                  value={newVoterPassword}
                  onChange={(e) => setNewVoterPassword(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px', fontFamily: 'monospace', letterSpacing: '0.5px' }}
                />
                <span style={{ fontSize: '11px', color: '#8E8E93', marginTop: '4px', display: 'block' }}>
                  Randomised 7-digit mixed uppercase letters and numbers (excluding look-alikes like 0/O, 1/I/L).
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                    Level
                  </label>
                  <select
                    value={newVoterLevel}
                    onChange={(e) => setNewVoterLevel(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                  >
                    <option value="100">100L</option>
                    <option value="200">200L</option>
                    <option value="300">300L</option>
                    <option value="400">400L</option>
                    <option value="500">500L</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                    Gender
                  </label>
                  <select
                    value={newVoterGender}
                    onChange={(e) => setNewVoterGender(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Hall of Residence
                </label>
                <input
                  type="text"
                  placeholder="Independence Hall"
                  value={newVoterHall}
                  onChange={(e) => setNewVoterHall(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              <button
                type="submit"
                style={{
                  marginTop: '10px',
                  padding: '11px',
                  borderRadius: '6px',
                  backgroundColor: '#FF9900',
                  color: '#171619',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Enroll Voter
              </button>
            </form>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL: EDIT ACCREDITED VOTER                                        */}
      {/* =================================================================== */}
      {editVoterModalOpen && editingVoter && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div style={{ backgroundColor: '#171619', border: '1px solid #2D2C31', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <button
              onClick={() => { setEditVoterModalOpen(false); setEditingVoter(null); }}
              style={{ position: 'absolute', right: '20px', top: '20px', background: 'transparent', border: 'none', color: '#8E8E93', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 4px 0', color: '#FFFFFF' }}>
              Edit Accredited Voter
            </h3>
            <p style={{ color: '#8E8E93', fontSize: '12.5px', margin: '0 0 18px 0' }}>
              Update student information, voting credentials, or demographic attributes.
            </p>

            <form onSubmit={handleSaveEditVoter} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Matric Number *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 210045"
                  value={editVoterMatric}
                  onChange={(e) => setEditVoterMatric(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Student Full Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Damilola Alabi"
                  value={editVoterName}
                  onChange={(e) => setEditVoterName(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="damilola@student.ui.edu.ng"
                  value={editVoterEmail}
                  onChange={(e) => setEditVoterEmail(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA' }}>
                    Voting Password / Access Code
                  </label>
                  <button
                    type="button"
                    onClick={() => setEditVoterPassword(generateVoterPassword())}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#FF9900',
                      fontSize: '11.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <RotateCcw size={12} /> Regenerate Code
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="7-digit code (e.g. 7M3H2WT)"
                  value={editVoterPassword}
                  onChange={(e) => setEditVoterPassword(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px', fontFamily: 'monospace', letterSpacing: '0.5px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                    Level
                  </label>
                  <select
                    value={editVoterLevel}
                    onChange={(e) => setEditVoterLevel(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                  >
                    <option value="100">100L</option>
                    <option value="200">200L</option>
                    <option value="300">300L</option>
                    <option value="400">400L</option>
                    <option value="500">500L</option>
                    <option value="600">600L</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                    Gender
                  </label>
                  <select
                    value={editVoterGender}
                    onChange={(e) => setEditVoterGender(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                    Hall of Residence
                  </label>
                  <input
                    type="text"
                    placeholder="Independence Hall"
                    value={editVoterHall}
                    onChange={(e) => setEditVoterHall(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                    Department
                  </label>
                  <input
                    type="text"
                    placeholder="Faculty of Law"
                    value={editVoterDepartment}
                    onChange={(e) => setEditVoterDepartment(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#A1A1AA', display: 'block', marginBottom: '6px' }}>
                  Vote Status
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setEditVoterHasVoted(false)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: editVoterHasVoted ? '1px solid #2D2C31' : '1px solid #EF4444',
                      backgroundColor: editVoterHasVoted ? '#0E0D0F' : 'rgba(239, 68, 68, 0.15)',
                      color: editVoterHasVoted ? '#8E8E93' : '#EF4444',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    NOT VOTED
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditVoterHasVoted(true)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: !editVoterHasVoted ? '1px solid #2D2C31' : '1px solid #10B981',
                      backgroundColor: !editVoterHasVoted ? '#0E0D0F' : 'rgba(16, 185, 129, 0.15)',
                      color: !editVoterHasVoted ? '#8E8E93' : '#10B981',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    VOTED
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => { setEditVoterModalOpen(false); setEditingVoter(null); }}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: '6px',
                    backgroundColor: '#2D2C31',
                    color: '#FFFFFF',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editVoterLoading}
                  style={{
                    flex: 2,
                    padding: '11px',
                    borderRadius: '6px',
                    backgroundColor: '#FF9900',
                    color: '#171619',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: editVoterLoading ? 'not-allowed' : 'pointer',
                    opacity: editVoterLoading ? 0.7 : 1,
                  }}
                >
                  {editVoterLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL: SET RESULTS PASSWORD                                         */}
      {/* =================================================================== */}
      {passwordModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div style={{ backgroundColor: '#171619', border: '1px solid #2D2C31', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '420px', position: 'relative' }}>
            <button
              onClick={() => setPasswordModalOpen(false)}
              style={{ position: 'absolute', right: '20px', top: '20px', background: 'transparent', border: 'none', color: '#8E8E93', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 8px 0' }}>
              Password Protect Live Results
            </h3>
            <p style={{ color: '#8E8E93', fontSize: '13px', margin: '0 0 16px 0' }}>
              Only observers and stakeholders with this password can view live tally charts.
            </p>

            <input
              type="text"
              placeholder="Enter results password (e.g. ElectpollResults25)"
              value={resultsPasswordInput}
              onChange={(e) => setResultsPasswordInput(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '14px', marginBottom: '16px' }}
            />

            <button
              onClick={() => handleSaveResultsProtection(true)}
              style={{
                width: '100%',
                padding: '11px',
                borderRadius: '6px',
                backgroundColor: '#FF9900',
                color: '#171619',
                border: 'none',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Set Password & Protect
            </button>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL: OFFICIAL CERTIFIED PDF REPORT EXPORT                         */}
      {/* =================================================================== */}
      <OfficialReportPdfModal
        isOpen={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        isAdmin={true}
        election={{
          ...election,
          commissionerName: settingsCommName,
          commissionerTitle: settingsCommTitle,
          commissionerSignatureUrl: settingsCommSig,
          observerName: settingsObsName,
          observerTitle: settingsObsTitle,
          observerSignatureUrl: settingsObsSig,
          electpollRepName: settingsElectpollRepName,
          electpollRepTitle: settingsElectpollRepTitle,
          electpollRepSignatureUrl: settingsElectpollRepSig,
          logo1Url: settingsLogo1,
          logo2Url: settingsLogo2,
        }}
        analytics={analytics}
        liveResults={liveResults}
        onUpdateLogos={handleUpdateElectionLogos}
        onUpdateSignatures={handleUpdateElectionSignatures}
      />
    </div>
  );
};
